import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import SerialProductDetails, {
  calculateDueDate,
  formatDisplayDate
} from "../components/SerialProductDetails";
import EngineerLayout from "../components/EngineerLayout";
import PhoneActions from "../components/PhoneActions";
import { openReportPdf } from "../utils/reportApi";
import { appendAuthToken } from "../utils/auth";
import {
  CONTACT_ROLE_OPTIONS,
  createEmptyContact,
  getCustomerContacts,
  getPrimaryCustomerContact,
  mapCustomerToForm,
  sanitizeCustomerPayload
} from "../utils/customerForm";

const COMPONENT_NAME_ALIASES = {
  "c.t": "CT",
  "c.t.": "CT",
  ct: "CT",
  "current transformer": "CT"
};

const FILLER_PATTERNS = [
  /\bneeds?\s+to\s+be\s+replac(?:e|ed)\b/gi,
  /\bneed\s+replacement\b/gi,
  /\bto\s+be\s+replac(?:e|ed)\b/gi,
  /\bare\s+failure\b/gi,
  /\bis\s+failure\b/gi,
  /\bfailure\b/gi,
  /\bfailed\b/gi,
  /\breplac(?:e|ed|ement)\b/gi,
  /\bissue\b/gi,
  /\bproblem\b/gi
];

const TITLE_CASE_EXCEPTIONS = new Set(["CT"]);

const formatTitleCase = (value = "") =>
  String(value || "")
    .split(" ")
    .filter(Boolean)
    .map((word) => {
      const upperWord = word.toUpperCase();
      if (TITLE_CASE_EXCEPTIONS.has(upperWord)) return upperWord;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");

const normalizeComponentName = (value = "") => {
  const compact = String(value || "")
    .replace(/[()[\]{}]/g, " ")
    .replace(/[.:;]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!compact) return "";

  const aliasKey = compact.toLowerCase();
  if (COMPONENT_NAME_ALIASES[aliasKey]) {
    return COMPONENT_NAME_ALIASES[aliasKey];
  }

  return formatTitleCase(compact);
};

const parseComponentEntry = (value = "") => {
  let source = String(value || "").trim();
  if (!source) return null;

  const quantityMatch = source.match(/\b(?:all\s+)?(\d+)\b/i);
  const quantity = quantityMatch ? Number(quantityMatch[1]) : 1;
  if (quantityMatch) {
    source = source.replace(quantityMatch[0], " ");
  }

  FILLER_PATTERNS.forEach((pattern) => {
    source = source.replace(pattern, " ");
  });

  source = source
    .replace(/\b(all|are|is|the|a|an|of|for|with|has|have|had)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  const normalizedName = normalizeComponentName(source);
  if (!normalizedName) return null;

  return {
    item: normalizedName,
    qty: quantity > 0 ? quantity : 1
  };
};

const formatUnit = (value = "") => {
  const unit = String(value || "").trim();
  if (!unit || unit.toLowerCase() === "pcs" || unit.toLowerCase() === "unit") return "No";
  return unit.charAt(0).toUpperCase() + unit.slice(1).toLowerCase();
};

const getFrameAndPole = (item = {}, prod = {}) => {
  if (item?.frameAndPole) return String(item.frameAndPole).trim();
  if (item?.frame_and_pole) return String(item.frame_and_pole).trim();
  if (prod?.frameAndPole) return String(prod.frameAndPole).trim();
  if (prod?.frame_and_pole) return String(prod.frame_and_pole).trim();

  const frame = String(item?.frame || prod?.frame || prod?.frameOther || prod?.frameRating || prod?.frameSize || "").trim();
  let pole = String(item?.pole || prod?.pole || prod?.numberOfPoles || "").trim();

  if (frame && pole) {
    if (!pole.toUpperCase().endsWith("P")) {
      pole = `${pole}P`;
    }
    return `${frame}/${pole}`;
  }
  if (frame) return frame;
  if (pole) {
    return pole.toUpperCase().endsWith("P") ? pole : `${pole}P`;
  }
  return "-";
};

const formatExcelDate = (dateVal) => {
  if (!dateVal) return "-";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return String(dateVal);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const createEmptyEarthPitForm = () => ({
  testedOn: "",
  dueDate: "",
  entries: [
    {
      earthPitNo: "",
      individualResistance: "",
      combinedResistance: "",
      additionalInfo: ""
    }
  ]
});

const formatDate = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const formatAddress = (address) => {
  if (!address) return "-";

  if (typeof address === "string") return address;

  return [
    address.street,
    address.area,
    address.city,
    address.state,
    address.pincode
  ].filter(Boolean).join(", ") || "-";
};

const belongsToCustomer = (record, routeCustomerId, customer) => {
  const recordCustomerId = String(record?.customerId || "").trim();
  const customerIdentifiers = [
    routeCustomerId,
    customer?._id,
    customer?.customerId
  ]
    .filter(Boolean)
    .map((value) => String(value).trim());

  if (recordCustomerId && customerIdentifiers.includes(recordCustomerId)) {
    return true;
  }

  const custName = String(customer?.name || "").trim().toLowerCase();
  if (custName) {
    const recordName = String(
      record?.company ||
      record?.customerName ||
      record?.counterpartyName ||
      ""
    ).trim().toLowerCase();
    if (recordName && recordName === custName) {
      return true;
    }
  }

  return false;
};

const normalizeStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();

  if (!normalized) return "Pending";
  if (normalized === "created") return "Assigned";
  if (normalized === "closed") return "Completed";
  if (["revisit", "re visit", "re-visit"].includes(normalized)) return "Re-Visit";
  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
};

const statusStyles = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "completed") {
    return "bg-emerald-100 text-emerald-700";
  }

  if (normalized === "in progress") {
    return "bg-blue-100 text-blue-700";
  }

  if (normalized === "re-visit" || normalized === "revisit" || normalized === "re visit") {
    return "bg-rose-100 text-rose-700";
  }

  if (normalized === "assigned" || normalized === "created") {
    return "bg-violet-100 text-violet-700";
  }

  return "bg-amber-100 text-amber-700";
};

const formatDocumentStatus = (value = "") => {
  const normalized = String(value || "").trim();
  if (!normalized) return "Draft";
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const getDocumentStatusStyles = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (["accepted", "paid", "completed", "invoiced"].includes(normalized)) {
    return "bg-emerald-100 text-emerald-700";
  }
  if (["sent", "partial", "partially_paid", "partially paid"].includes(normalized)) {
    return "bg-sky-100 text-sky-700";
  }
  if (["rejected", "overdue", "cancelled"].includes(normalized)) {
    return "bg-rose-100 text-rose-700";
  }
  if (normalized === "due today") {
    return "bg-amber-100 text-amber-700";
  }
  return "bg-amber-100 text-amber-700";
};

const getQuotationTotalAmount = (quotation = {}) => Number(quotation.grandTotal ?? quotation.subTotal ?? 0);
const getSalesInvoiceTotalAmount = (invoice = {}) => Number(invoice.finalTotal ?? invoice.totalAmount ?? 0);
const getSalesInvoiceAmountPaid = (invoice = {}) => Number(invoice.advanceAmount ?? invoice.amountPaid ?? 0);
const getSalesInvoiceBalancePending = (invoice = {}) => {
  const total = getSalesInvoiceTotalAmount(invoice);
  const paid = getSalesInvoiceAmountPaid(invoice);
  const noteAdjustment = Number(invoice.noteAdjustmentAmount || 0);
  const calculated = Math.max(Number((total - paid - noteAdjustment).toFixed(2)), 0);

  const explicitBalance = invoice.balanceAmount;
  if (explicitBalance !== undefined && explicitBalance !== null && explicitBalance !== "") {
    const raw = Number(explicitBalance) || 0;
    if (paid > 0 && raw >= total) return calculated;
    if (raw === 0 && paid < total) return calculated;
    return Math.min(Math.max(raw, 0), calculated);
  }
  return calculated;
};

const formatPaymentStatusLabel = (invoice = {}) => {
  const normalized = String(invoice.paymentStatus || "").trim().toLowerCase();
  if (normalized === "paid") return "Paid";
  if (["partially_paid", "partial"].includes(normalized)) return "Partially Paid";
  if (normalized === "overdue") return "Overdue";
  if (normalized === "due_today") return "Due Today";

  const balance = getSalesInvoiceBalancePending(invoice);
  const paid = getSalesInvoiceAmountPaid(invoice);
  if (balance <= 0) return "Paid";
  if (paid > 0) return "Partially Paid";
  return "Pending";
};

const buildComplaintEmailSubject = (customer, row) => {
  const companyName = customer?.name || row?.company || "Customer";
  const complaintId = row?.complaintId || "Complaint";
  return `${companyName} - Service Report Update - ${complaintId}`;
};

const buildComplaintEmailBody = (customer, row) => {
  const lines = [
    `Customer: ${customer?.name || row?.company || "-"}`,
    `Customer ID: ${customer?.customerId || "-"}`,
    `Complaint ID: ${row?.complaintId || "-"}`,
    `Complaint Status: ${normalizeStatus(row?.complaintStatus)}`,
    `Report Status: ${normalizeStatus(row?.finalStatus)}`,
    `Report ID: ${row?.reportId || "-"}`,
    `Visit Date: ${formatDate(row?.visitDate)}`,
    `Last Updated: ${formatDate(row?.updatedAt)}`,
    `Engineer: ${row?.engineer || "-"}`,
    `Product Type: ${row?.productType || "-"}`,
    `Contact Person: ${row?.contactName || customer?.contactPerson || "-"}`,
    `Contact Phone: ${row?.contactPhone || customer?.phone || "-"}`,
    `Customer Email: ${customer?.email || customer?.additionalDetails?.alternateEmail || "-"}`,
    `Address: ${formatAddress(customer?.address)}`,
    "",
    "Complaint Details:",
    row?.issue || "-",
    ""
  ];

  if (row?.observation) {
    lines.push("Observation:");
    lines.push(row.observation);
    lines.push("");
  }

  if (row?.actionTaken) {
    lines.push("Action Taken:");
    lines.push(row.actionTaken);
    lines.push("");
  }

  if (row?.reviewRemarks) {
    lines.push("Review Remarks:");
    lines.push(row.reviewRemarks);
    lines.push("");
  }

  lines.push("Regards,");
  lines.push("Genmech Service");

  return lines.join("\n");
};

const getItemsSummary = (items) => {
  if (!items || !items.length) return "-";
  const firstItem = items[0].description || items[0].itemCode || "-";
  if (items.length > 1) {
    return `${firstItem} (+${items.length - 1} more)`;
  }
  return firstItem;
};

export default function CustomerDetails() {
  const role = localStorage.getItem("role") || "";
  const location = useLocation();
  const canManageCustomer = role === "admin";
  const isAccountsView = role === "accounts";
  const isEngineerRoute = location.pathname.startsWith("/engineer");
  const isEngineerView = role === "engineer" && isEngineerRoute;
  const engineerLookup =
    localStorage.getItem("engineerId") ||
    localStorage.getItem("engineerEmail") ||
    localStorage.getItem("engineerName") ||
    "";
  const { id } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [visits, setVisits] = useState([]);
  const [materialEntries, setMaterialEntries] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [salesInvoices, setSalesInvoices] = useState([]);
  const [customerProducts, setCustomerProducts] = useState([]);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [productTypeFilter, setProductTypeFilter] = useState("All");
  const [productPanelAreaFilter, setProductPanelAreaFilter] = useState("");
  const [productSortBy, setProductSortBy] = useState("serviceDate-desc");
  const [loading, setLoading] = useState(true);
  const [viewingQuotation, setViewingQuotation] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const initialActiveSection = useMemo(() => {
    const requested = location.state?.activeSection;
    if (requested === "products" || requested === "components-to-replace" || requested === "components-replaced") {
      return "complaints-reports";
    }
    return requested || "customer-details";
  }, [location.state?.activeSection]);

  const initialComplaintsReportsSubTab = useMemo(() => {
    const requested = location.state?.activeSection;
    if (requested === "products" || requested === "components-to-replace" || requested === "components-replaced") {
      return requested;
    }
    return location.state?.complaintsReportsSubTab || "complaints-reports";
  }, [location.state?.activeSection, location.state?.complaintsReportsSubTab]);

  const [activeSection, setActiveSection] = useState(initialActiveSection);
  const [complaintsReportsSubTab, setComplaintsReportsSubTab] = useState(initialComplaintsReportsSubTab);
  const [quotationSubTab, setQuotationSubTab] = useState("quotations");
  const [quotedProductSearch, setQuotedProductSearch] = useState("");
  const [quotedProductDateFrom, setQuotedProductDateFrom] = useState("");
  const [quotedProductDateTo, setQuotedProductDateTo] = useState("");
  const [quotedProductQuotationNo, setQuotedProductQuotationNo] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState("");
  const [showSupplyForm, setShowSupplyForm] = useState(false);
  const [supplySaving, setSupplySaving] = useState(false);
  const [earthPitEntries, setEarthPitEntries] = useState([]);
  const [earthPitSort, setEarthPitSort] = useState("testedOn-desc");
  const [earthPitFilterDate, setEarthPitFilterDate] = useState("");
  const [showEarthPitForm, setShowEarthPitForm] = useState(false);
  const [earthPitSaving, setEarthPitSaving] = useState(false);
  const [earthPitForm, setEarthPitForm] = useState(createEmptyEarthPitForm);
  const [emailDraft, setEmailDraft] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContactIndex, setEditingContactIndex] = useState(-1);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [contactRoleFilter, setContactRoleFilter] = useState("All");
  const [contactForm, setContactForm] = useState(createEmptyContact(true));
  const [supplyForm, setSupplyForm] = useState({
    itemName: "",
    sku: "",
    quantity: "",
    unit: "No",
    location: "",
    reference: "",
    transactionDate: new Date().toISOString().slice(0, 10),
    notes: ""
  });

  const [componentSearch, setComponentSearch] = useState("");
  const [componentProductFilter, setComponentProductFilter] = useState("All");
  const [componentSortBy, setComponentSortBy] = useState("visitDate-desc");

  const [replacedComponentSearch, setReplacedComponentSearch] = useState("");
  const [replacedComponentProductFilter, setReplacedComponentProductFilter] = useState("All");
  const [replacedComponentSortBy, setReplacedComponentSortBy] = useState("visitDate-desc");

  const sortedEarthPitEntries = useMemo(() => {
    const sorted = [...earthPitEntries].filter((entry) => {
      if (!earthPitFilterDate) return true;

      const testedOn = entry?.testedOn ? new Date(entry.testedOn) : null;
      if (!testedOn || Number.isNaN(testedOn.getTime())) return false;

      const year = testedOn.getFullYear();
      const month = String(testedOn.getMonth() + 1).padStart(2, "0");
      const day = String(testedOn.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}` === earthPitFilterDate;
    });

    switch (earthPitSort) {
      case "testedOn-asc":
        sorted.sort((a, b) => new Date(a.testedOn || 0) - new Date(b.testedOn || 0));
        break;
      case "earthPitNo-asc":
        sorted.sort((a, b) => String(a.earthPitNo || "").localeCompare(String(b.earthPitNo || ""), undefined, { numeric: true, sensitivity: "base" }));
        break;
      case "earthPitNo-desc":
        sorted.sort((a, b) => String(b.earthPitNo || "").localeCompare(String(a.earthPitNo || ""), undefined, { numeric: true, sensitivity: "base" }));
        break;
      case "testedOn-desc":
      default:
        sorted.sort((a, b) => new Date(b.testedOn || 0) - new Date(a.testedOn || 0));
        break;
    }

    return sorted;
  }, [earthPitEntries, earthPitSort, earthPitFilterDate]);

  const customerContacts = useMemo(() => getCustomerContacts(customer), [customer]);
  const primaryContact = useMemo(() => getPrimaryCustomerContact(customer), [customer]);
  const visibleContacts = useMemo(() => {
    const query = contactSearch.trim().toLowerCase();

    return customerContacts.filter((contact) => {
      const role = String(contact.role || "").toLowerCase();
      const name = String(contact.name || "").toLowerCase();
      const mobileNumber = String(contact.mobileNumber || "").toLowerCase();

      const matchesSearch = !query || [role, name, mobileNumber].some((value) => value.includes(query));
      const matchesRole = contactRoleFilter === "All" || contact.role === contactRoleFilter;

      return matchesSearch && matchesRole;
    });
  }, [contactRoleFilter, contactSearch, customerContacts]);

  const openContactModal = (contact = createEmptyContact(false), index = -1) => {
    setEditingContactIndex(index);
    setContactForm({
      ...createEmptyContact(index === -1 ? customerContacts.length === 0 : contact.isPrimary),
      ...contact
    });
    setContactModalOpen(true);
  };

  const closeContactModal = () => {
    setContactModalOpen(false);
    setEditingContactIndex(-1);
    setContactForm(createEmptyContact(true));
  };

  const persistContacts = async (nextContacts) => {
    if (!customer) return;

    setContactSaving(true);
    try {
      const nextCustomer = {
        ...customer,
        additionalContacts: nextContacts
      };
      const payload = sanitizeCustomerPayload(mapCustomerToForm(nextCustomer));
      const response = await axios.put(API_BASE_URL + `/api/customers/${id}`, payload);
      const updatedCustomer = response.data || nextCustomer;
      setCustomer(updatedCustomer);
      setContactModalOpen(false);
      setEditingContactIndex(-1);
      setContactForm(createEmptyContact(true));
      alert("Contacts updated successfully.");
    } catch (error) {
      alert(error.response?.data?.message || error.message || "Failed to update contacts");
    } finally {
      setContactSaving(false);
    }
  };

  const saveContact = async () => {
    const nextContact = {
      ...contactForm,
      role: String(contactForm.role || "Other").trim() || "Other",
      name: String(contactForm.name || "").trim(),
      mobileNumber: String(contactForm.mobileNumber || "").trim(),
      email: String(contactForm.email || "").trim(),
      whatsappNumber: String(contactForm.whatsappNumber || "").trim(),
      remarks: String(contactForm.remarks || "").trim(),
      isPrimary: Boolean(contactForm.isPrimary)
    };

    const nextContacts = editingContactIndex >= 0
      ? customerContacts.map((contact, index) => (index === editingContactIndex ? nextContact : contact))
      : [...customerContacts, nextContact];

    if (nextContact.isPrimary) {
      nextContacts.forEach((contact, index) => {
        nextContacts[index] = {
          ...contact,
          isPrimary: index === (editingContactIndex >= 0 ? editingContactIndex : nextContacts.length - 1)
        };
      });
    } else if (!nextContacts.some((contact) => contact.isPrimary)) {
      nextContacts[0] = { ...nextContacts[0], isPrimary: true };
    }

    await persistContacts(nextContacts);
  };

  const deleteContact = async (index) => {
    const nextContacts = customerContacts.filter((_, contactIndex) => contactIndex !== index);
    if (nextContacts.length === 0) {
      nextContacts.push(createEmptyContact(true));
    }
    if (!nextContacts.some((contact) => contact.isPrimary)) {
      nextContacts[0] = { ...nextContacts[0], isPrimary: true };
    }
    await persistContacts(nextContacts);
  };

  const buildEarthPitPdfUrl = () => {
    const params = new URLSearchParams();
    if (earthPitFilterDate) {
      params.set("date", earthPitFilterDate);
    }

    const query = params.toString();
    return API_BASE_URL + `/api/earth-pits/customer/${id}/pdf${query ? `?${query}` : ""}`;
  };

  const openEarthPitPdf = () => {
    window.open(appendAuthToken(buildEarthPitPdfUrl()), "_blank", "noopener,noreferrer");
  };

  const generateEarthPitPdf = () => {
    const link = document.createElement("a");
    const params = new URLSearchParams();
    if (earthPitFilterDate) {
      params.set("date", earthPitFilterDate);
    }
    params.set("download", "1");
    link.href = appendAuthToken(API_BASE_URL + `/api/earth-pits/customer/${id}/pdf?${params.toString()}`);
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const wrapWithLayout = (content) =>
    isEngineerView ? <EngineerLayout>{content}</EngineerLayout> : content;

  useEffect(() => {
    if (role === "engineer" && !isEngineerRoute) {
      navigate(`/engineer/customer/${id}`, { replace: true });
    }
  }, [id, isEngineerRoute, navigate, role]);

  useEffect(() => {
    let isMounted = true;
    const requestConfig = { timeout: 15000 };

    const get = (path, config = {}) =>
      axios.get(API_BASE_URL + path, { ...config, timeout: requestConfig.timeout });

    const fetchData = async () => {
      setLoading(true);
      setAccessDenied(false);
      setAccessDeniedMessage("");
      setCustomer(null);
      setComplaints([]);
      setVisits([]);
      setMaterialEntries([]);
      setQuotations([]);
      setSalesInvoices([]);
      setEarthPitEntries([]);
      setCustomerProducts([]);

      try {
        const primaryRequests = [get(`/api/customers/${id}`)];
        if (isEngineerView && engineerLookup) {
          primaryRequests.push(get(`/api/engineers/profile/${encodeURIComponent(engineerLookup)}`));
        }

        const [customerRes, engineerProfileRes] = await Promise.all(primaryRequests);

        if (isEngineerView) {
          const engineerProfile = engineerProfileRes?.data || null;
          const customerViewEnabled = Boolean(engineerProfile?.customerViewEnabled);

          if (!customerViewEnabled) {
            if (isMounted) {
              setAccessDenied(true);
              setAccessDeniedMessage("Admin has hidden customer profile and product details for your login.");
              setCustomer(null);
              setComplaints([]);
              setVisits([]);
              setMaterialEntries([]);
              setQuotations([]);
              setSalesInvoices([]);
              setEarthPitEntries([]);
              setCustomerProducts([]);
            }
            return;
          }
        }

        if (isMounted) {
          // Render the profile as soon as its own record is available. History panels
          // are optional and must never keep the entire page on the loading screen.
          setCustomer(customerRes.data || null);
          setLoading(false);
        }

        const historyRequests = [
          { key: "complaints", request: get("/api/complaints", { params: { customerId: id } }) },
          { key: "earthPits", request: get("/api/earth-pits", { params: { customerId: id } }) },
          { key: "products", request: get(`/api/customers/${id}/products`) }
        ];

        if (canManageCustomer || isAccountsView) {
          historyRequests.push(
            { key: "material", request: get("/api/material-stock", { params: { customerId: id } }) },
            { key: "quotations", request: get("/api/quotations", { params: { customerId: id } }) },
            { key: "salesInvoices", request: get("/api/account-documents", { params: { customerId: id, entryType: "sale" } }) }
          );
        }

        const settledHistory = await Promise.allSettled(historyRequests.map(({ request }) => request));
        const history = historyRequests.reduce((result, { key }, index) => {
          const response = settledHistory[index];
          result[key] = response.status === "fulfilled" ? response.value.data : [];
          return result;
        }, {});
        const failedHistory = historyRequests
          .filter((_, index) => settledHistory[index].status === "rejected")
          .map(({ key }) => key);

        if (failedHistory.length) {
          console.warn("Some customer history sections could not be loaded:", failedHistory.join(", "));
        }

        const complaintData = Array.isArray(history.complaints) ? history.complaints : [];
        const complaintIds = complaintData.flatMap((complaint) => [complaint._id, complaint.complaintId]).filter(Boolean);
        let visitData = [];

        if (complaintIds.length) {
          try {
            const visitsRes = await get("/api/visits", {
              params: { complaintIds: complaintIds.join(","), excludePdf: 1 }
            });
            visitData = Array.isArray(visitsRes.data) ? visitsRes.data : [];
          } catch (error) {
            console.warn("Customer visits could not be loaded:", error);
          }
        }

        if (isMounted) {
          setComplaints(complaintData);
          setVisits(visitData);
          setMaterialEntries(Array.isArray(history.material) ? history.material : []);
          setQuotations(Array.isArray(history.quotations) ? history.quotations : []);
          setSalesInvoices(Array.isArray(history.salesInvoices) ? history.salesInvoices : []);
          setEarthPitEntries(Array.isArray(history.earthPits) ? history.earthPits : []);
          setCustomerProducts(Array.isArray(history.products) ? history.products : []);
        }
      } catch (error) {
        console.error("Failed to load customer details", error);
        if (isMounted) {
          alert("Failed to load customer details");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [canManageCustomer, engineerLookup, id, isAccountsView, isEngineerView]);

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Delete this customer?");
    if (!confirmDelete) return;

    try {
      await axios.delete(API_BASE_URL + `/api/customers/${id}`);
      alert("Deleted successfully");
      navigate(canManageCustomer ? "/customers" : isAccountsView ? "/accounts/customers" : "/engineer-complaints");
    } catch (error) {
      alert("Error deleting");
    }
  };

  const refreshMaterialEntries = async () => {
    try {
      const materialRes = await axios.get(API_BASE_URL + "/api/material-stock", {
        params: { customerId: id }
      });
      setMaterialEntries(Array.isArray(materialRes.data) ? materialRes.data : []);
    } catch (error) {
      console.error("Failed to load material history", error);
    }
  };

  const refreshSalesInvoices = async () => {
    try {
      const salesInvoiceRes = await axios.get(API_BASE_URL + "/api/account-documents", {
        params: { customerId: id, entryType: "sale" }
      });
      setSalesInvoices(Array.isArray(salesInvoiceRes.data) ? salesInvoiceRes.data : []);
    } catch (error) {
      console.error("Failed to load sales invoices", error);
    }
  };

  const handleSupplyFormChange = (event) => {
    const { name, value } = event.target;
    setSupplyForm((current) => ({
      ...current,
      [name]: value
    }));
  };

  const handleEarthPitEntryChange = (index, name, value) => {
    setEarthPitForm((current) => ({
      ...current,
      entries: current.entries.map((entry, entryIndex) => {
        if (entryIndex !== index) return entry;

        return {
          ...entry,
          [name]: value
        };
      })
    }));
  };

  const handleEarthPitCommonChange = (event) => {
    const { name, value } = event.target;

    setEarthPitForm((current) => ({
      ...current,
      [name]: value,
      ...(name === "testedOn"
        ? {
            dueDate: value
              ? calculateDueDate(value) === "-"
                ? ""
                : (() => {
                    const testedOnDate = new Date(`${value}T00:00:00`);
                    testedOnDate.setFullYear(testedOnDate.getFullYear() + 1);
                    testedOnDate.setDate(testedOnDate.getDate() - 1);
                    const year = testedOnDate.getFullYear();
                    const month = String(testedOnDate.getMonth() + 1).padStart(2, "0");
                    const day = String(testedOnDate.getDate()).padStart(2, "0");
                    return `${year}-${month}-${day}`;
                  })()
              : ""
          }
        : {})
    }));
  };

  const addEarthPitEntryRow = () => {
    setEarthPitForm((current) => ({
      ...current,
      entries: [
        ...current.entries,
        {
          earthPitNo: "",
          individualResistance: "",
          combinedResistance: "",
          additionalInfo: ""
        }
      ]
    }));
  };

  const removeEarthPitEntryRow = (index) => {
    setEarthPitForm((current) => ({
      ...current,
      entries:
        current.entries.length === 1
          ? current.entries
          : current.entries.filter((_, entryIndex) => entryIndex !== index)
    }));
  };

  const handleSupplySubmit = async (event) => {
    event.preventDefault();

    try {
      setSupplySaving(true);
      await axios.post(API_BASE_URL + "/api/material-stock", {
        ...supplyForm,
        entryType: "sale",
        customerId: id,
        counterpartyName: customer?.name || "",
        quantity: Number(supplyForm.quantity)
      });

      setSupplyForm({
        itemName: "",
        sku: "",
        quantity: "",
        unit: "No",
        location: "",
        reference: "",
        transactionDate: new Date().toISOString().slice(0, 10),
        notes: ""
      });
      setShowSupplyForm(false);
      await refreshMaterialEntries();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save material supply");
    } finally {
      setSupplySaving(false);
    }
  };

  const handleEarthPitSubmit = async (event) => {
    event.preventDefault();

    try {
      setEarthPitSaving(true);

      const response = await axios.post(API_BASE_URL + "/api/earth-pits", {
        engineerId: engineerLookup,
        engineerName: localStorage.getItem("engineerName") || "",
        customerId: id,
        testedOn: earthPitForm.testedOn,
        entries: earthPitForm.entries.map((entry) => ({
          earthPitNo: entry.earthPitNo,
          individualResistance: entry.individualResistance,
          combinedResistance: entry.combinedResistance,
          additionalInfo: entry.additionalInfo
        }))
      });

      const savedEntries = Array.isArray(response.data) ? response.data : [response.data];
      setEarthPitEntries((current) => [...savedEntries, ...current]);
      setEarthPitForm(createEmptyEarthPitForm());
      setShowEarthPitForm(false);
      alert("Earth pit details saved successfully.");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save earth pit details");
    } finally {
      setEarthPitSaving(false);
    }
  };

  const customerComplaints = useMemo(
    () => complaints.filter((item) => belongsToCustomer(item, id, customer)),
    [complaints, customer, id]
  );

  const customerQuotations = useMemo(
    () => quotations.filter((item) => belongsToCustomer(item, id, customer)),
    [customer, id, quotations]
  );

  const quotedProductsList = useMemo(() => {
    const list = [];
    customerQuotations.forEach((quotation) => {
      const qDate = quotation.quotationDate;
      const qNo = quotation.quotationNo || "Draft";
      const qId = quotation._id;
      if (Array.isArray(quotation.items)) {
        quotation.items.forEach((item) => {
          list.push({
            quotationId: qId,
            quotationDate: qDate,
            quotationNo: qNo,
            productName: item.description || "",
            itemCode: item.itemCode || "",
            hsnCode: item.hsnCode || "",
            quantity: item.quantity || 0,
            unit: item.unit || "No",
            unitPrice: item.unitPrice || 0,
            totalPrice: item.totalAmount || 0,
            originalItem: item,
            originalQuotation: quotation
          });
        });
      }
    });

    // Default sorting: Newest quotation date first
    return list.sort((a, b) => new Date(b.quotationDate || 0) - new Date(a.quotationDate || 0));
  }, [customerQuotations]);

  const filteredQuotedProducts = useMemo(() => {
    let list = [...quotedProductsList];

    // Search by Product Name, Item Code, HSN Code, Quotation Number
    const searchVal = quotedProductSearch.trim().toLowerCase();
    if (searchVal) {
      list = list.filter((item) => 
        String(item.productName).toLowerCase().includes(searchVal) ||
        String(item.itemCode).toLowerCase().includes(searchVal) ||
        String(item.hsnCode).toLowerCase().includes(searchVal) ||
        String(item.quotationNo).toLowerCase().includes(searchVal)
      );
    }

    // Filter by Date Range
    if (quotedProductDateFrom) {
      const fromDate = new Date(quotedProductDateFrom);
      list = list.filter((item) => {
        const itemDate = new Date(item.quotationDate);
        return itemDate >= fromDate;
      });
    }

    if (quotedProductDateTo) {
      const toDate = new Date(quotedProductDateTo);
      toDate.setHours(23, 59, 59, 999);
      list = list.filter((item) => {
        const itemDate = new Date(item.quotationDate);
        return itemDate <= toDate;
      });
    }

    // Filter by Quotation Number
    const qNoFilter = quotedProductQuotationNo.trim().toLowerCase();
    if (qNoFilter) {
      list = list.filter((item) => 
        String(item.quotationNo).toLowerCase().includes(qNoFilter)
      );
    }

    return list;
  }, [quotedProductsList, quotedProductSearch, quotedProductDateFrom, quotedProductDateTo, quotedProductQuotationNo]);

  const customerSalesInvoices = useMemo(
    () => salesInvoices.filter((item) => belongsToCustomer(item, id, customer)),
    [customer, id, salesInvoices]
  );

  const groupedCustomerSalesInvoices = useMemo(() => {
    const groups = new Map();

    customerSalesInvoices.forEach((invoice) => {
      const docNo = String(invoice.documentNumber || "").trim();
      const groupKey = docNo
        ? `${invoice.entryType || "sale"}::doc::${docNo.toLowerCase()}`
        : String(invoice.documentGroupKey || invoice._id || "");
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          ...invoice,
          lineItems: [invoice]
        });
        return;
      }

      const current = groups.get(groupKey);
      if (!current.lineItems.some((item) => String(item._id) === String(invoice._id))) {
        current.lineItems.push(invoice);
      }
    });

    return Array.from(groups.values()).sort((a, b) => new Date(b.transactionDate || b.createdAt || 0) - new Date(a.transactionDate || a.createdAt || 0));
  }, [customerSalesInvoices]);

  const customerDocumentSummary = useMemo(() => ({
    totalQuotations: customerQuotations.length,
    totalSalesInvoices: groupedCustomerSalesInvoices.length,
    balancePending: groupedCustomerSalesInvoices.reduce((sum, invoice) => sum + getSalesInvoiceBalancePending(invoice), 0)
  }), [customerQuotations, groupedCustomerSalesInvoices]);

  const effectiveCustomerProducts = useMemo(() => {
    const map = new Map();
    // 1. Products from customerProducts endpoint
    (customerProducts || []).forEach((pRecord) => {
      if (!pRecord) return;
      const prod = pRecord.product || {};
      const v = pRecord.visit || {};
      const key = `${prod.serialNo || ""}_${prod.productName || prod.product || ""}_${v.visitId || v._id || ""}`;
      map.set(key, pRecord);
    });

    // 2. Products directly from Complaints & Reports visits
    (visits || []).forEach((visit) => {
      if (!visit || !Array.isArray(visit.products)) return;
      visit.products.forEach((prod, idx) => {
        if (!prod) return;
        const key = `${prod.serialNo || ""}_${prod.productName || prod.product || ""}_${visit.visitId || visit._id || ""}`;
        if (!map.has(key)) {
          map.set(key, {
            product: prod,
            visit: {
              _id: visit._id,
              visitId: visit.visitId || visit.reportId || `Report #${idx + 1}`,
              reportId: visit.reportId || visit.visitId,
              visitDate: visit.visitDate || visit.date,
              engineerName: visit.engineerName,
              status: visit.status,
              approvalStatus: visit.approvalStatus,
              company: visit.company || customer?.name,
              contactPerson: visit.contactPerson,
              contactPhone: visit.contactPhone,
              address: visit.address,
              scopeOfWork: visit.scopeOfWork,
              findings: visit.findings,
              actionTaken: visit.actionTaken
            }
          });
        }
      });
    });

    return Array.from(map.values());
  }, [customerProducts, visits, customer]);

  const filteredCustomerProducts = useMemo(() => {
    let list = [...effectiveCustomerProducts];

    const searchVal = productSearch.trim().toLowerCase();
    if (searchVal) {
      list = list.filter((pRecord) => {
        const prod = pRecord.product || {};
        return (
          String(prod.productName || prod.product || "").toLowerCase().includes(searchVal) ||
          String(prod.serialNo || "").toLowerCase().includes(searchVal) ||
          String(prod.panelArea || "").toLowerCase().includes(searchVal) ||
          String(prod.feeder || prod.feederName || "").toLowerCase().includes(searchVal)
        );
      });
    }

    if (productTypeFilter !== "All") {
      list = list.filter((pRecord) => {
        const prod = pRecord.product || {};
        const pName = String(prod.productName || prod.product || "").toLowerCase();
        const pCat = String(prod.category || "").toLowerCase();

        if (productTypeFilter === "acb") {
          return pCat === "breaker" && pName.includes("acb");
        }
        if (productTypeFilter === "mccb") {
          return pCat === "breaker" && pName.includes("mccb");
        }
        if (productTypeFilter === "vcb") {
          return pCat === "breaker" && pName.includes("vcb");
        }
        if (productTypeFilter === "other") {
          return !pName.includes("acb") && !pName.includes("mccb") && !pName.includes("vcb");
        }
        return true;
      });
    }

    const panelVal = productPanelAreaFilter.trim().toLowerCase();
    if (panelVal) {
      list = list.filter((pRecord) => {
        const prod = pRecord.product || {};
        return String(prod.panelArea || "").toLowerCase().includes(panelVal);
      });
    }

    list.sort((a, b) => {
      const prodA = a.product || {};
      const prodB = b.product || {};

      if (productSortBy === "serviceDate-desc") {
        return new Date(b.visit.visitDate || 0) - new Date(a.visit.visitDate || 0);
      }
      if (productSortBy === "serviceDate-asc") {
        return new Date(a.visit.visitDate || 0) - new Date(b.visit.visitDate || 0);
      }
      if (productSortBy === "dueDate-asc") {
        const parseDate = (dStr) => {
          if (!dStr) return new Date(0);
          const parts = dStr.split("-");
          if (parts.length === 3) {
            return new Date(parts[2], parts[1] - 1, parts[0]);
          }
          return new Date(dStr);
        };
        return parseDate(a.visit.visitDate || "") - parseDate(b.visit.visitDate || "");
      }
      if (productSortBy === "productName-asc") {
        const nameA = String(prodA.productName || prodA.product || "");
        const nameB = String(prodB.productName || prodB.product || "");
        return nameA.localeCompare(nameB);
      }
      if (productSortBy === "panelArea-asc") {
        const panelA = String(prodA.panelArea || "");
        const panelB = String(prodB.panelArea || "");
        return panelA.localeCompare(panelB);
      }
      return 0;
    });

    return list;
  }, [effectiveCustomerProducts, productSearch, productTypeFilter, productPanelAreaFilter, productSortBy]);


  const historyRows = useMemo(() => {
    return customerComplaints
      .map((complaint) => {
        const linkedVisits = visits
          .filter((visit) => {
            const visitComplaintId = String(visit.complaintId || "");
            return (
              visitComplaintId === String(complaint._id) ||
              visitComplaintId === String(complaint.complaintId)
            );
          })
          .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));

        const latestVisit = linkedVisits[0] || null;
        const visitProducts = Array.isArray(latestVisit?.products) ? latestVisit.products : [];

        const visitReplacedComponents = [];
        const visitToBeReplacedComponents = [];

        visitProducts.forEach((prod) => {
          if (!prod) return;

          // 1. Replaced components
          const compsReplaced = Array.isArray(prod.componentsReplaced)
            ? prod.componentsReplaced.filter(Boolean)
            : [];
          compsReplaced.forEach((c) => {
            const rawName = typeof c === "string" ? c : (c.name || c.componentName || "");
            if (rawName) {
              const parsed = parseComponentEntry(rawName);
              const qty = typeof c === "object" && c.quantity ? Number(c.quantity) : (parsed ? parsed.qty : 1);
              visitReplacedComponents.push({
                name: parsed ? parsed.item : rawName,
                quantity: qty > 0 ? qty : 1,
                productName: prod.productName || prod.product || "",
                serialNo: prod.serialNo || ""
              });
            }
          });

          const newComp = Array.isArray(prod.newComp)
            ? prod.newComp.filter(Boolean)
            : (typeof prod.newComp === "string" && prod.newComp.trim() ? [prod.newComp] : []);
          newComp.forEach((compText) => {
            String(compText || "")
              .split(/,|;|\n/gi)
              .map((s) => s.trim())
              .filter(Boolean)
              .forEach((part) => {
                const parsed = parseComponentEntry(part);
                visitReplacedComponents.push({
                  name: parsed ? parsed.item : part,
                  quantity: parsed ? parsed.qty : 1,
                  productName: prod.productName || prod.product || "",
                  serialNo: prod.serialNo || ""
                });
              });
          });

          // 2. To be replaced components
          const compsToBe = Array.isArray(prod.componentsToBeReplaced)
            ? prod.componentsToBeReplaced.filter(Boolean)
            : [];
          compsToBe.forEach((c) => {
            if (String(c.status || "").trim().toLowerCase() === "replaced") {
              const rawName = c.name || c.componentName || "";
              if (rawName) {
                const parsed = parseComponentEntry(rawName);
                visitReplacedComponents.push({
                  name: parsed ? parsed.item : rawName,
                  quantity: c.quantity ? Number(c.quantity) : (parsed ? parsed.qty : 1),
                  productName: prod.productName || prod.product || "",
                  serialNo: prod.serialNo || ""
                });
              }
              return;
            }
            const rawName = c.name || c.componentName || "";
            if (rawName) {
              const parsed = parseComponentEntry(rawName);
              visitToBeReplacedComponents.push({
                name: parsed ? parsed.item : rawName,
                quantity: c.quantity ? Number(c.quantity) : (parsed ? parsed.qty : 1),
                productName: prod.productName || prod.product || "",
                serialNo: prod.serialNo || ""
              });
            }
          });

          const compReplace = Array.isArray(prod.compReplace)
            ? prod.compReplace.filter(Boolean)
            : [];
          compReplace.forEach((compText) => {
            String(compText || "")
              .split(/,|;|\n/gi)
              .map((s) => s.trim())
              .filter(Boolean)
              .forEach((part) => {
                const parsed = parseComponentEntry(part);
                visitToBeReplacedComponents.push({
                  name: parsed ? parsed.item : part,
                  quantity: parsed ? parsed.qty : 1,
                  productName: prod.productName || prod.product || "",
                  serialNo: prod.serialNo || ""
                });
              });
          });
        });

        return {
          complaintDbId: complaint._id,
          customerId: complaint.customerId,
          complaintId: complaint.complaintId || "-",
          issue: complaint.issue || "-",
          productType: complaint.productType || "",
          engineer: latestVisit?.engineer || complaint.engineerAssigned || "-",
          engineerAssigned: complaint.engineerAssigned || latestVisit?.engineer || "",
          complaintStatus: complaint.status || "pending",
          reportId: latestVisit?.reportId || "-",
          finalStatus: latestVisit?.finalStatus || complaint.status || "pending",
          visitDate: latestVisit?.visitDate || complaint.visitDate || complaint.createdAt,
          updatedAt: latestVisit?.updatedAt || latestVisit?.createdAt || complaint.updatedAt || complaint.createdAt,
          visitId: latestVisit?._id || "",
          hasPdf: Boolean(latestVisit?._id),
          contactName: latestVisit?.contactName || complaint.contactName || customer?.contactPerson || "",
          contactPhone: latestVisit?.phone || complaint.contactPhone || customer?.phone || "",
          contactEmail: latestVisit?.email || customer?.email || customer?.additionalDetails?.alternateEmail || "",
          observation: latestVisit?.observation || "",
          actionTaken: latestVisit?.actionTaken || "",
          reviewRemarks: latestVisit?.reviewRemarks || "",
          company: complaint.company || customer?.name || "",
          replacedComponents: visitReplacedComponents,
          componentsToBeReplaced: visitToBeReplacedComponents
        };
      })
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  }, [customer?.additionalDetails?.alternateEmail, customer?.contactPerson, customer?.email, customer?.name, customer?.phone, customerComplaints, visits]);

  const openReportEmailDraft = (row) => {
    const defaultEmail =
      row?.contactEmail ||
      customer?.email ||
      customer?.additionalDetails?.alternateEmail ||
      "";

    setEmailDraft({
      row,
      to: defaultEmail,
      subject: buildComplaintEmailSubject(customer, row),
      body: buildComplaintEmailBody(customer, row)
    });
  };

  const sendReportEmail = () => {
    if (!emailDraft) return;

    if (!String(emailDraft.to || "").trim()) {
      alert("Please enter an email address");
      return;
    }

    if (!emailDraft.row?.visitId) {
      alert("A submitted report is required before sending email with PDF attachment");
      return;
    }

    setSendingEmail(true);
    axios.post(API_BASE_URL + `/api/customers/${id}/send-report-email`, {
      to: emailDraft.to,
      subject: emailDraft.subject,
      message: emailDraft.body,
      complaintId: emailDraft.row.complaintDbId,
      visitId: emailDraft.row.visitId
    })
      .then((response) => {
        alert(response.data?.message || "Report email sent successfully");
        setEmailDraft(null);
      })
      .catch((error) => {
        alert(error.response?.data?.message || "Failed to send report email");
      })
      .finally(() => {
        setSendingEmail(false);
      });
  };

  const quotationPagePath = canManageCustomer ? "/admin/quotations" : isAccountsView ? "/accounts/quotations" : "";
  const salesInvoicePagePath = canManageCustomer ? "/admin/sales-invoices" : isAccountsView ? "/accounts/sales-invoices" : "";

  const openQuotationPdf = (quotationId) => {
    window.open(appendAuthToken(API_BASE_URL + `/api/quotations/${quotationId}/pdf`), "_blank", "noopener,noreferrer");
  };

  const openSalesInvoicePdf = (invoiceId) => {
    window.open(appendAuthToken(API_BASE_URL + `/api/account-documents/${invoiceId}/pdf`), "_blank", "noopener,noreferrer");
  };

  const handleEditQuotation = (quotation) => {
    if (!quotationPagePath) return;

    navigate(quotationPagePath, {
      state: {
        quotationEdit: quotation,
        prefillKey: `quotation-edit-${quotation._id}-${Date.now()}`,
        fromPath: location.pathname
      }
    });
  };

  const handleDuplicateQuotation = (quotation) => {
    if (!quotationPagePath) return;

    navigate(quotationPagePath, {
      state: {
        duplicateQuotationPrefill: quotation,
        prefillKey: `duplicate-${quotation._id}-${Date.now()}`,
        fromPath: location.pathname
      }
    });
  };

  const handleReQuotation = (quotation) => {
    if (!quotationPagePath) return;

    navigate(quotationPagePath, {
      state: {
        reQuotationPrefill: {
          ...quotation,
          customerId: quotation.customerId || customer?._id || id,
          customerName: quotation.customerName || customer?.name
        },
        prefillKey: `requote-${quotation._id}-${Date.now()}`,
        fromPath: location.pathname
      }
    });
  };

  const handleStatusChange = async (quotationId, newStatus) => {
    try {
      await axios.put(`${API_BASE_URL}/api/quotations/${quotationId}/status`, { status: newStatus });
      setQuotations((prev) =>
        prev.map((q) => (q._id === quotationId ? { ...q, status: newStatus } : q))
      );
    } catch (error) {
      console.error("Failed to update quotation status", error);
      alert(error.response?.data?.message || "Failed to update quotation status");
    }
  };

  const handleEditSalesInvoice = (invoice) => {
    if (!salesInvoicePagePath) return;

    navigate(salesInvoicePagePath, {
      state: {
        accountDocumentEditEntry: invoice,
        prefillKey: `sales-invoice-edit-${invoice._id}-${Date.now()}`
      }
    });
  };

  const handleDeleteSalesInvoice = async (invoice) => {
    if (!window.confirm(`Delete sales invoice ${invoice.documentNumber || ""}?`)) {
      return;
    }

    try {
      await axios.delete(API_BASE_URL + `/api/account-documents/${invoice._id}`);
      await refreshSalesInvoices();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete sales invoice");
    }
  };

  const handleRecordPayment = (invoice) => {
    handleEditSalesInvoice(invoice);
  };

  const replacementComponents = useMemo(() => {
    const list = [];
    const seen = new Set();

    effectiveCustomerProducts.forEach((pRecord) => {
      const prod = pRecord.product || {};
      const visit = pRecord.visit || {};
      const visitId = visit._id || visit.visitId || "";
      const reportId = visit.reportId || visit.visitId || "View";
      const visitDate = visit.visitDate || visit.date || "";

      // 1. Structured componentsToBeReplaced
      const structuredToBeReplaced = Array.isArray(prod.componentsToBeReplaced)
        ? prod.componentsToBeReplaced.filter(Boolean)
        : [];

      structuredToBeReplaced.forEach((item) => {
        const status = String(item.status || "").trim().toLowerCase();
        if (status === "replaced") return;

        const rawText = String(item.name || item.componentName || "").trim();
        if (!rawText) return;

        const parsed = parseComponentEntry(rawText);
        const name = parsed ? parsed.item : rawText;
        const qty = item.quantity ? Number(item.quantity) : (parsed ? parsed.qty : 1);
        const dedupeKey = `${visitId}_${prod.serialNo}_${name.toLowerCase()}`;

        if (!seen.has(dedupeKey)) {
          seen.add(dedupeKey);
          list.push({
            productName: prod.productName || prod.product || "-",
            serialNo: prod.serialNo || "N/A",
            category: prod.category || "-",
            panelArea: prod.panelArea || "-",
            originalText: rawText,
            componentName: name,
            frameAndPole: getFrameAndPole(item, prod),
            quantity: qty > 0 ? qty : 1,
            remarks: item.remarks || "",
            status: item.status || "To Be Replaced",
            visitId,
            reportId,
            visitDate: item.identifiedDate || visitDate,
            productRecord: pRecord
          });
        }
      });

      // 2. Legacy / text array compReplace
      const compReplace = Array.isArray(prod.compReplace)
        ? prod.compReplace.filter(Boolean)
        : [];
      
      compReplace.forEach((compText) => {
        const subParts = String(compText || "")
          .split(/,|;|\n/gi)
          .map((item) => item.trim())
          .filter(Boolean);

        subParts.forEach((part) => {
          const parsed = parseComponentEntry(part);
          const name = parsed ? parsed.item : part;
          const dedupeKey = `${visitId}_${prod.serialNo}_${name.toLowerCase()}`;

          if (!seen.has(dedupeKey)) {
            seen.add(dedupeKey);
            list.push({
              productName: prod.productName || prod.product || "-",
              serialNo: prod.serialNo || "N/A",
              category: prod.category || "-",
              panelArea: prod.panelArea || "-",
              originalText: part,
              componentName: name,
              frameAndPole: getFrameAndPole({}, prod),
              quantity: parsed ? parsed.qty : 1,
              remarks: "",
              status: "To Be Replaced",
              visitId,
              reportId,
              visitDate,
              productRecord: pRecord
            });
          }
        });
      });
    });
    return list;
  }, [effectiveCustomerProducts]);

  const uniqueReplacementProducts = useMemo(() => {
    const set = new Set();
    replacementComponents.forEach((c) => {
      if (c.productName) set.add(c.productName);
    });
    return Array.from(set).sort();
  }, [replacementComponents]);

  const filteredReplacementComponents = useMemo(() => {
    let result = [...replacementComponents];

    const query = componentSearch.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (c) =>
          c.componentName.toLowerCase().includes(query) ||
          c.originalText.toLowerCase().includes(query) ||
          c.productName.toLowerCase().includes(query) ||
          c.serialNo.toLowerCase().includes(query) ||
          (c.remarks && c.remarks.toLowerCase().includes(query))
      );
    }

    if (componentProductFilter !== "All") {
      result = result.filter((c) => c.productName === componentProductFilter);
    }

    switch (componentSortBy) {
      case "visitDate-asc":
        result.sort((a, b) => new Date(a.visitDate || 0) - new Date(b.visitDate || 0));
        break;
      case "quantity-desc":
        result.sort((a, b) => b.quantity - a.quantity);
        break;
      case "quantity-asc":
        result.sort((a, b) => a.quantity - b.quantity);
        break;
      case "name-asc":
        result.sort((a, b) => a.componentName.localeCompare(b.componentName));
        break;
      case "visitDate-desc":
      default:
        result.sort((a, b) => new Date(b.visitDate || 0) - new Date(a.visitDate || 0));
        break;
    }

    return result;
  }, [replacementComponents, componentSearch, componentProductFilter, componentSortBy]);

  const componentSummary = useMemo(() => {
    const summary = {};
    filteredReplacementComponents.forEach((c) => {
      const name = c.componentName;
      summary[name] = (summary[name] || 0) + c.quantity;
    });
    return Object.entries(summary).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [filteredReplacementComponents]);

  const replacedComponents = useMemo(() => {
    const list = [];
    const seen = new Set();

    effectiveCustomerProducts.forEach((pRecord) => {
      const prod = pRecord.product || {};
      const visit = pRecord.visit || {};
      const visitId = visit._id || visit.visitId || "";
      const reportId = visit.reportId || visit.visitId || "View";
      const visitDate = visit.visitDate || visit.date || "";
      const engineer = visit.engineerName || visit.engineer || "";

      // 1. Structured componentsReplaced (modern)
      const structuredReplaced = Array.isArray(prod.componentsReplaced)
        ? prod.componentsReplaced.filter(Boolean)
        : [];

      structuredReplaced.forEach((item) => {
        const rawText = typeof item === "string" ? item : (item.name || item.componentName || "");
        if (!rawText) return;

        const parsed = parseComponentEntry(rawText);
        const name = parsed ? parsed.item : rawText;
        const qty = typeof item === "object" && item.quantity ? Number(item.quantity) : (parsed ? parsed.qty : 1);
        const rDate = typeof item === "object" && item.replacementDate ? item.replacementDate : visitDate;
        const remarks = typeof item === "object" ? (item.remarks || "") : "";
        const previousPart = typeof item === "object" ? (item.previousPart || "") : "";
        const newPart = typeof item === "object" ? (item.newPart || "") : "";
        const dedupeKey = `${visitId}_${prod.serialNo}_${name.toLowerCase()}_${rDate}`;

        if (!seen.has(dedupeKey)) {
          seen.add(dedupeKey);
          list.push({
            productName: prod.productName || prod.product || "-",
            serialNo: prod.serialNo || "N/A",
            category: prod.category || "-",
            panelArea: prod.panelArea || "-",
            originalText: rawText,
            componentName: name,
            frameAndPole: getFrameAndPole(typeof item === "object" ? item : {}, prod),
            quantity: qty > 0 ? qty : 1,
            previousPart,
            newPart,
            remarks,
            replacementDate: rDate,
            visitId,
            reportId,
            visitDate,
            engineer,
            productRecord: pRecord
          });
        }
      });

      // 2. Legacy / text array newComp
      const newComp = Array.isArray(prod.newComp)
        ? prod.newComp.filter(Boolean)
        : (typeof prod.newComp === "string" && prod.newComp.trim() ? [prod.newComp] : []);

      newComp.forEach((compText) => {
        const subParts = String(compText || "")
          .split(/,|;|\n/gi)
          .map((item) => item.trim())
          .filter(Boolean);

        subParts.forEach((part) => {
          const parsed = parseComponentEntry(part);
          const name = parsed ? parsed.item : part;
          const dedupeKey = `${visitId}_${prod.serialNo}_${name.toLowerCase()}_${visitDate}`;

          if (!seen.has(dedupeKey)) {
            seen.add(dedupeKey);
            list.push({
              productName: prod.productName || prod.product || "-",
              serialNo: prod.serialNo || "N/A",
              category: prod.category || "-",
              panelArea: prod.panelArea || "-",
              originalText: part,
              componentName: name,
              frameAndPole: getFrameAndPole({}, prod),
              quantity: parsed ? parsed.qty : 1,
              previousPart: "",
              newPart: "",
              remarks: "",
              replacementDate: visitDate,
              visitId,
              reportId,
              visitDate,
              engineer,
              productRecord: pRecord
            });
          }
        });
      });

      // 3. Components marked as "Replaced" inside componentsToBeReplaced
      const structuredToBeReplaced = Array.isArray(prod.componentsToBeReplaced)
        ? prod.componentsToBeReplaced.filter(Boolean)
        : [];

      structuredToBeReplaced.forEach((item) => {
        if (!item || typeof item !== "object") return;
        const status = String(item.status || "").trim().toLowerCase();
        if (status === "replaced") {
          const rawText = String(item.name || item.componentName || "").trim();
          if (!rawText) return;

          const parsed = parseComponentEntry(rawText);
          const name = parsed ? parsed.item : rawText;
          const qty = item.quantity ? Number(item.quantity) : (parsed ? parsed.qty : 1);
          const rDate = item.replacedDate || item.replacementDate || visitDate;
          const dedupeKey = `${visitId}_${prod.serialNo}_${name.toLowerCase()}_${rDate}`;

          if (!seen.has(dedupeKey)) {
            seen.add(dedupeKey);
            list.push({
              productName: prod.productName || prod.product || "-",
              serialNo: prod.serialNo || "N/A",
              category: prod.category || "-",
              panelArea: prod.panelArea || "-",
              originalText: rawText,
              componentName: name,
              quantity: qty > 0 ? qty : 1,
              previousPart: item.previousPart || "",
              newPart: item.newPart || "",
              remarks: item.remarks || "",
              replacementDate: rDate,
              visitId,
              reportId,
              visitDate,
              engineer,
              productRecord: pRecord
            });
          }
        }
      });
    });

    return list;
  }, [effectiveCustomerProducts]);

  const uniqueReplacedProducts = useMemo(() => {
    const set = new Set();
    replacedComponents.forEach((c) => {
      if (c.productName) set.add(c.productName);
    });
    return Array.from(set).sort();
  }, [replacedComponents]);

  const filteredReplacedComponents = useMemo(() => {
    let result = [...replacedComponents];

    const query = replacedComponentSearch.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (c) =>
          c.componentName.toLowerCase().includes(query) ||
          c.originalText.toLowerCase().includes(query) ||
          c.productName.toLowerCase().includes(query) ||
          c.serialNo.toLowerCase().includes(query) ||
          (c.remarks && c.remarks.toLowerCase().includes(query)) ||
          (c.previousPart && c.previousPart.toLowerCase().includes(query)) ||
          (c.newPart && c.newPart.toLowerCase().includes(query)) ||
          (c.engineer && c.engineer.toLowerCase().includes(query))
      );
    }

    if (replacedComponentProductFilter !== "All") {
      result = result.filter((c) => c.productName === replacedComponentProductFilter);
    }

    switch (replacedComponentSortBy) {
      case "visitDate-asc":
        result.sort((a, b) => new Date(a.replacementDate || a.visitDate || 0) - new Date(b.replacementDate || b.visitDate || 0));
        break;
      case "quantity-desc":
        result.sort((a, b) => b.quantity - a.quantity);
        break;
      case "quantity-asc":
        result.sort((a, b) => a.quantity - b.quantity);
        break;
      case "name-asc":
        result.sort((a, b) => a.componentName.localeCompare(b.componentName));
        break;
      case "visitDate-desc":
      default:
        result.sort((a, b) => new Date(b.replacementDate || b.visitDate || 0) - new Date(a.replacementDate || a.visitDate || 0));
        break;
    }

    return result;
  }, [replacedComponents, replacedComponentSearch, replacedComponentProductFilter, replacedComponentSortBy]);

  const replacedComponentSummary = useMemo(() => {
    const summary = {};
    filteredReplacedComponents.forEach((c) => {
      const name = c.componentName;
      summary[name] = (summary[name] || 0) + c.quantity;
    });
    return Object.entries(summary).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [filteredReplacedComponents]);

  const handleExportReplacementComponentsExcel = () => {
    if (!filteredReplacementComponents.length) {
      alert("No replacement components to export.");
      return;
    }

    const workbook = XLSX.utils.book_new();

    // ── 1. Detailed Items sheet: "To Be Replaced" (Matches Image 1) ──
    const titleRow = ["", "", "", "To Be Replaced", "", "", "", ""];
    const headerRow = [
      "S.No",
      "Defective Component",
      "Frame and Pole",
      "No. of Items",
      "Product Name",
      "Serial Number",
      "Panel Area",
      "Service Date"
    ];

    const dataRows = filteredReplacementComponents.map((item, idx) => [
      idx + 1,
      item.componentName || "-",
      item.frameAndPole || "-",
      Number(item.quantity) || 1,
      item.productName || "-",
      item.serialNo || "-",
      item.panelArea || "-",
      formatExcelDate(item.visitDate)
    ]);

    const totalQty = filteredReplacementComponents.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);
    const totalRow = [
      "",
      "TOTAL COMPONENTS",
      "",
      totalQty,
      "",
      "",
      "",
      ""
    ];

    const sheet1Data = [titleRow, headerRow, ...dataRows, totalRow];
    const worksheet = XLSX.utils.aoa_to_sheet(sheet1Data);

    const totalRowIdx = sheet1Data.length - 1;
    worksheet["!merges"] = [
      // Top title merged across A1:H1
      { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } },
      // "TOTAL COMPONENTS" merged across Col B & Col C (c: 1 to c: 2)
      { s: { r: totalRowIdx, c: 1 }, e: { r: totalRowIdx, c: 2 } }
    ];

    worksheet["!cols"] = [
      { wch: 8 },  // S.No
      { wch: 32 }, // Defective Component
      { wch: 18 }, // Frame and Pole
      { wch: 14 }, // No. of Items
      { wch: 30 }, // Product Name
      { wch: 18 }, // Serial Number
      { wch: 22 }, // Panel Area
      { wch: 16 }  // Service Date
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, "To Be Replaced");

    // ── 2. Aggregated Summary sheet (Matches Image 2) ──
    // Group by Component Name, then by Frame and Pole
    const compMap = new Map();
    filteredReplacementComponents.forEach((item) => {
      const compName = item.componentName || "Unknown Component";
      const framePole = item.frameAndPole || "-";
      const qty = Number(item.quantity) || 1;

      if (!compMap.has(compName)) {
        compMap.set(compName, new Map());
      }
      const frameMap = compMap.get(compName);
      frameMap.set(framePole, (frameMap.get(framePole) || 0) + qty);
    });

    const summaryDataRows = [];
    let compIndex = 1;
    for (const [compName, frameMap] of compMap.entries()) {
      const currentSNo = compIndex;
      for (const [framePole, qty] of frameMap.entries()) {
        summaryDataRows.push([
          currentSNo,
          compName,
          framePole,
          qty
        ]);
      }
      compIndex++;
    }

    const summaryGrandTotal = summaryDataRows.reduce((sum, r) => sum + Number(r[3] || 0), 0);
    const summaryTotalRow = [
      "",
      "GRAND TOTAL",
      "",
      summaryGrandTotal
    ];

    const sheet2Data = [
      ["S.No", "Component Name", "Frame and Pole", "Total Quantity Required"],
      ...summaryDataRows,
      summaryTotalRow
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(sheet2Data);
    const summaryTotalRowIdx = sheet2Data.length - 1;
    summarySheet["!merges"] = [
      { s: { r: summaryTotalRowIdx, c: 1 }, e: { r: summaryTotalRowIdx, c: 2 } }
    ];

    summarySheet["!cols"] = [
      { wch: 8 },  // S.No
      { wch: 34 }, // Component Name
      { wch: 18 }, // Frame and Pole
      { wch: 26 }  // Total Quantity Required
    ];

    XLSX.utils.book_append_sheet(workbook, summarySheet, "Aggregated Summary");

    const customerSlug = (customer?.name || "Customer").replace(/[^a-zA-Z0-9_-]/g, "_");
    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `${customerSlug}_Components_To_Be_Replaced_${dateStr}.xlsx`);
  };

  const handleExportReplacedComponentsExcel = () => {
    if (!filteredReplacedComponents.length) {
      alert("No replaced components to export.");
      return;
    }

    const workbook = XLSX.utils.book_new();

    // ── 1. Detailed Items sheet: "Components Replaced" ──
    const titleRow = ["", "", "", "Components Replaced", "", "", "", ""];
    const headerRow = [
      "S.No",
      "Replaced Component",
      "Frame and Pole",
      "No. of Items",
      "Product Name",
      "Serial Number",
      "Panel Area",
      "Service Date"
    ];

    const dataRows = filteredReplacedComponents.map((item, idx) => [
      idx + 1,
      item.componentName || "-",
      item.frameAndPole || "-",
      Number(item.quantity) || 1,
      item.productName || "-",
      item.serialNo || "-",
      item.panelArea || "-",
      formatExcelDate(item.replacementDate || item.visitDate)
    ]);

    const totalQty = filteredReplacedComponents.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);
    const totalRow = [
      "",
      "TOTAL COMPONENTS",
      "",
      totalQty,
      "",
      "",
      "",
      ""
    ];

    const sheet1Data = [titleRow, headerRow, ...dataRows, totalRow];
    const worksheet = XLSX.utils.aoa_to_sheet(sheet1Data);

    const totalRowIdx = sheet1Data.length - 1;
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } },
      { s: { r: totalRowIdx, c: 1 }, e: { r: totalRowIdx, c: 2 } }
    ];

    worksheet["!cols"] = [
      { wch: 8 },  // S.No
      { wch: 32 }, // Replaced Component
      { wch: 18 }, // Frame and Pole
      { wch: 14 }, // No. of Items
      { wch: 30 }, // Product Name
      { wch: 18 }, // Serial Number
      { wch: 22 }, // Panel Area
      { wch: 16 }  // Service Date
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, "Components Replaced");

    // ── 2. Aggregated Summary sheet ──
    const compMap = new Map();
    filteredReplacedComponents.forEach((item) => {
      const compName = item.componentName || "Unknown Component";
      const framePole = item.frameAndPole || "-";
      const qty = Number(item.quantity) || 1;

      if (!compMap.has(compName)) {
        compMap.set(compName, new Map());
      }
      const frameMap = compMap.get(compName);
      frameMap.set(framePole, (frameMap.get(framePole) || 0) + qty);
    });

    const summaryDataRows = [];
    let compIndex = 1;
    for (const [compName, frameMap] of compMap.entries()) {
      const currentSNo = compIndex;
      for (const [framePole, qty] of frameMap.entries()) {
        summaryDataRows.push([
          currentSNo,
          compName,
          framePole,
          qty
        ]);
      }
      compIndex++;
    }

    const summaryGrandTotal = summaryDataRows.reduce((sum, r) => sum + Number(r[3] || 0), 0);
    const summaryTotalRow = [
      "",
      "GRAND TOTAL",
      "",
      summaryGrandTotal
    ];

    const sheet2Data = [
      ["S.No", "Component Name", "Frame and Pole", "Total Quantity Required"],
      ...summaryDataRows,
      summaryTotalRow
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(sheet2Data);
    const summaryTotalRowIdx = sheet2Data.length - 1;
    summarySheet["!merges"] = [
      { s: { r: summaryTotalRowIdx, c: 1 }, e: { r: summaryTotalRowIdx, c: 2 } }
    ];

    summarySheet["!cols"] = [
      { wch: 8 },  // S.No
      { wch: 34 }, // Component Name
      { wch: 18 }, // Frame and Pole
      { wch: 26 }  // Total Quantity Required
    ];

    XLSX.utils.book_append_sheet(workbook, summarySheet, "Aggregated Summary");

    const customerSlug = (customer?.name || "Customer").replace(/[^a-zA-Z0-9_-]/g, "_");
    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `${customerSlug}_Components_Replaced_${dateStr}.xlsx`);
  };

  const sectionTabs = useMemo(
    () => [
      { key: "customer-details", label: "Customer Details" },
      { key: "additional-contacts", label: "Additional Contacts" },
      { key: "complaints-reports", label: "Complaints & Reports" },
      { key: "quotations", label: "Quotation" },
      { key: "sales-invoices", label: "Sales Invoice" },
      { key: "earth-pit", label: "Earth Pit" },
      ...(isAccountsView ? [{ key: "sales", label: "Material Supply" }] : [])
    ],
    [isAccountsView]
  );

  useEffect(() => {
    if (!sectionTabs.some((section) => section.key === activeSection)) {
      setActiveSection(sectionTabs[0]?.key || "customer-details");
    }
  }, [activeSection, sectionTabs]);

  if (loading) {
    return wrapWithLayout(<div className="p-6">Loading...</div>);
  }

  if (accessDenied) {
    return wrapWithLayout(
      <div className="p-6">
        {accessDeniedMessage || "Access to this customer is not available."}
      </div>
    );
  }

  if (!customer) {
    return wrapWithLayout(<div className="p-6">Customer not found.</div>);
  }

  return wrapWithLayout(
    <div className="space-y-8">
      <section className="rounded-[32px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.13),_transparent_30%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(224,231,255,0.72))] p-6 shadow-sm lg:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">
                Customer Profile
              </p>
              {customer.customerId && (
                <span className="font-mono text-xs font-bold uppercase tracking-wider text-slate-500">
                  • {customer.customerId}
                </span>
              )}
              {customer.groupId?.groupName && (
                <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-700/10">
                  🏢 {customer.groupId.groupName}
                </span>
              )}
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-slate-900">{customer.name}</h1>
              {customer.customerId && (
                <span className="inline-flex items-center rounded-xl border border-sky-200 bg-sky-50 px-3 py-1 font-mono text-sm font-bold text-sky-700 shadow-xs">
                  {customer.customerId}
                </span>
              )}
            </div>
          </div>

        <div className="flex flex-wrap gap-2">
            {isAccountsView && (
              <button
                onClick={() => setShowSupplyForm((current) => !current)}
                className="rounded-2xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
              >
                {showSupplyForm ? "Close Material Supply" : "Add Material Supply"}
              </button>
            )}

            {(canManageCustomer || isAccountsView) && (
              <button
                onClick={() => navigate(isAccountsView ? `/accounts/edit-customer/${id}` : `/edit-customer/${id}`)}
                className="rounded-2xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
              >
                Edit
              </button>
            )}

            {canManageCustomer && (
              <button
                onClick={handleDelete}
                className="rounded-2xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                Delete
              </button>
            )}

            <button
              onClick={() => navigate(canManageCustomer ? "/customers" : isAccountsView ? "/accounts/customers" : "/engineer-complaints")}
              className="rounded-2xl bg-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-300"
            >
              Back
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6 lg:gap-4">
          <MetricCard label="Total Complaints" value={customerComplaints.length} tone="blue" />
          <MetricCard
            label="Completed Reports"
            value={historyRows.filter((row) => String(row.finalStatus).toLowerCase() === "completed").length}
            tone="emerald"
          />
          <MetricCard
            label="Pending Work"
            value={historyRows.filter((row) => String(row.finalStatus).toLowerCase() !== "completed").length}
            tone="amber"
          />
          <MetricCard label="Total Quotations" value={customerDocumentSummary.totalQuotations} tone="violet" />
          <MetricCard label="Total Sales Invoices" value={customerDocumentSummary.totalSalesInvoices} tone="blue" />
          <MetricCard label="Balance Pending" value={`Rs ${customerDocumentSummary.balancePending.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} tone="amber" />
        </div>
      </section>

      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap gap-3">
          {sectionTabs.map((section) => {
            const isActive = section.key === activeSection;
            return (
              <button
                key={section.key}
                type="button"
                onClick={() => setActiveSection(section.key)}
                className={`rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${
                  isActive
                    ? "bg-sky-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {section.label}
              </button>
            );
          })}
        </div>
      </section>

      {(activeSection === "customer-details" || activeSection === "additional-contacts") && (
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          {activeSection === "customer-details" && (
            <div>
              <h2 className="text-xl font-bold text-slate-900">Customer Details</h2>

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <DetailCard label="Customer ID" value={customer.customerId || "-"} />
                <DetailCard label="Email" value={customer.email || "-"} />
                <DetailCard label="Phone" value={<PhoneActions phone={customer.phone || ""} />} />
                <DetailCard
                  label="Primary Contact"
                  value={
                    primaryContact ? (
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-semibold text-sky-700">
                            {primaryContact.role || "Primary"}
                          </span>
                          <span className="text-sm font-semibold text-slate-900">
                            {primaryContact.name || "-"}
                          </span>
                        </div>
                        <div className="text-sm text-slate-600">
                          <PhoneActions
                            phone={primaryContact.mobileNumber || ""}
                            whatsappPhone={primaryContact.whatsappNumber || ""}
                          />
                        </div>
                        {primaryContact.email ? (
                          <p className="text-sm text-slate-600">{primaryContact.email}</p>
                        ) : null}
                      </div>
                    ) : (
                      "-"
                    )
                  }
                />
                <DetailCard
                  label="GST Type"
                  value={
                    customer.gstType === "sez_supply"
                      ? "SEZ SUPPLY"
                      : customer.gstType === "igst"
                        ? "IGST"
                        : "GST"
                  }
                />
                <DetailCard label="GST No" value={customer.gstNumber || customer.additionalDetails?.gstNumber || "-"} />
                {String(customer.gstType || "").trim().toLowerCase() === "sez_supply" ? (
                  <DetailCard label="LUT Ref No" value={customer.lutRefNo || "-"} />
                ) : null}
                <DetailCard label="Additional Info" value={customer.additionalInfo || "-"} />
                <DetailCard
                  label="Google Maps Location"
                  value={
                    customer.googleMapsLink ? (
                      <a
                        href={customer.googleMapsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-sky-600 hover:text-sky-800 hover:underline inline-flex items-center gap-1.5"
                      >
                        <svg className="h-4 w-4 shrink-0 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Open Google Maps &rarr;
                      </a>
                    ) : (
                      "Not set"
                    )
                  }
                />
                <DetailCard
                  label="Customer Group"
                  value={
                    customer.groupId ? (
                      <div className="space-y-1">
                        <span className="font-bold text-blue-600">{customer.groupId.groupName || "Group"}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const groupProfileId = customer.groupId._id || customer.groupId;
                            navigate(
                              isEngineerView
                                ? `/engineer/customer-groups/${groupProfileId}`
                                : isAccountsView
                                ? `/accounts/customer-groups/${groupProfileId}`
                                : `/customer-groups/${groupProfileId}`
                            );
                          }}
                          className="text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline text-left block mt-1"
                        >
                          View All Customers in Group &rarr;
                        </button>
                      </div>
                    ) : (
                      "None (Ungrouped)"
                    )
                  }
                />
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Billing Address</p>
                  <p className="mt-3 text-sm leading-6 text-slate-700">
                    {formatAddress(customer.billingAddress || customer.address)}
                  </p>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Work / Delivery Address</p>
                  <p className="mt-3 text-sm leading-6 text-slate-700">
                    {formatAddress(customer.address || customer.billingAddress)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeSection === "additional-contacts" && (
            <div className="space-y-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Additional Contacts</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Manage role-based contacts, mark a primary contact, and keep the same record available everywhere in the system.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openContactModal(createEmptyContact(customerContacts.length === 0), -1)}
                  className="rounded-2xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700"
                >
                  + Add Contact
                </button>
              </div>

              <div className="grid gap-3 md:grid-cols-[1.4fr_0.8fr]">
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    placeholder="Search by name, mobile number, or role"
                    value={contactSearch}
                    onChange={(event) => setContactSearch(event.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-9 pr-4 text-sm text-slate-700 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
                  />
                </div>

                <select
                  value={contactRoleFilter}
                  onChange={(event) => setContactRoleFilter(event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
                >
                  <option value="All">All Roles</option>
                  {CONTACT_ROLE_OPTIONS.map((roleOption) => (
                    <option key={roleOption} value={roleOption}>
                      {roleOption}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:hidden">
                {visibleContacts.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                    {customerContacts.length === 0 ? "No contacts saved yet." : "No contacts match your search."}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visibleContacts.map((contact, index) => {
                      const contactIndex = customerContacts.findIndex((item) => item === contact);
                      return (
                        <div
                          key={`${contact.role || "contact"}-${contact.name || index}-${contactIndex}`}
                          className="rounded-[22px] border border-slate-200 bg-slate-50 p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                                  {contact.role || "Other"}
                                </span>
                                {contact.isPrimary ? (
                                  <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                                    Primary
                                  </span>
                                ) : null}
                              </div>
                              <p className="mt-2 text-base font-semibold text-slate-900">{contact.name || "-"}</p>
                            </div>
                          </div>

                          <div className="mt-4 space-y-3">
                            <div className="text-sm text-slate-700">
                              <PhoneActions
                                phone={contact.mobileNumber || ""}
                                whatsappPhone={contact.whatsappNumber || ""}
                              />
                            </div>
                            {contact.email ? <p className="text-sm text-slate-600">{contact.email}</p> : null}
                            {contact.remarks ? <p className="text-sm text-slate-500">{contact.remarks}</p> : null}
                          </div>

                          <div className="mt-4 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => openContactModal(contact, contactIndex)}
                              className="rounded-2xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteContact(contactIndex)}
                              className="rounded-2xl bg-red-100 px-4 py-2 text-sm font-semibold text-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[1120px] text-left">
                  <thead className="border-b bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-sm font-semibold text-slate-600">Role</th>
                      <th className="px-4 py-3 text-sm font-semibold text-slate-600">Contact Person</th>
                      <th className="px-4 py-3 text-sm font-semibold text-slate-600">Mobile Number</th>
                      <th className="px-4 py-3 text-sm font-semibold text-slate-600">Email</th>
                      <th className="px-4 py-3 text-sm font-semibold text-slate-600">WhatsApp</th>
                      <th className="px-4 py-3 text-sm font-semibold text-slate-600">Remarks</th>
                      <th className="px-4 py-3 text-sm font-semibold text-slate-600">Status</th>
                      <th className="px-4 py-3 text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleContacts.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-500">
                          {customerContacts.length === 0 ? "No contacts saved yet." : "No contacts match your search."}
                        </td>
                      </tr>
                    ) : (
                      visibleContacts.map((contact, index) => {
                        const contactIndex = customerContacts.findIndex((item) => item === contact);
                        return (
                          <tr key={`${contact.role || "contact"}-${contact.name || index}-${contactIndex}`} className="border-b border-slate-200 align-top hover:bg-slate-50">
                            <td className="px-4 py-4">
                              <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
                                {contact.role || "Other"}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="font-semibold text-slate-900">{contact.name || "-"}</div>
                              {contact.isPrimary ? (
                                <span className="mt-2 inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                                  Primary Contact
                                </span>
                              ) : null}
                            </td>
                            <td className="px-4 py-4 text-slate-700">
                              <PhoneActions
                                phone={contact.mobileNumber || ""}
                                whatsappPhone={contact.whatsappNumber || ""}
                              />
                            </td>
                            <td className="px-4 py-4 text-slate-700">{contact.email || "-"}</td>
                            <td className="px-4 py-4 text-slate-700">{contact.whatsappNumber || "-"}</td>
                            <td className="px-4 py-4 text-slate-700">{contact.remarks || "-"}</td>
                            <td className="px-4 py-4 text-slate-700">
                              <button
                                type="button"
                                onClick={() => persistContacts(customerContacts.map((item, itemIndex) => ({
                                  ...item,
                                  isPrimary: itemIndex === contactIndex
                                })))}
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${contact.isPrimary ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                              >
                                {contact.isPrimary ? "Primary" : "Make Primary"}
                              </button>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => openContactModal(contact, contactIndex)}
                                  className="rounded-2xl bg-sky-600 px-3.5 py-2 text-sm font-semibold text-white"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteContact(contactIndex)}
                                  className="rounded-2xl bg-red-100 px-3.5 py-2 text-sm font-semibold text-red-700"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {activeSection === "quotations" && (
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          {/* Sub-tab Switcher Bar */}
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-6">
            <button
              type="button"
              onClick={() => setQuotationSubTab("quotations")}
              className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
                quotationSubTab === "quotations"
                  ? "bg-sky-600 text-white shadow-sm border-sky-600"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Quotations
            </button>
            <button
              type="button"
              onClick={() => setQuotationSubTab("products")}
              className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
                quotationSubTab === "products"
                  ? "bg-sky-600 text-white shadow-sm border-sky-600"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Products
            </button>
          </div>

          {quotationSubTab === "quotations" ? (
            <>
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Quotation</h2>
                  <p className="mt-1 text-sm text-slate-500">Only quotations created for this customer are shown here.</p>
                </div>
                <div className="rounded-full bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700">
                  {customerQuotations.length} Quotations
                </div>
              </div>

              <div className="mt-6 overflow-x-auto">
                {customerQuotations.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
                    No quotations found for this customer yet.
                  </div>
                ) : (
                  <table className="w-full min-w-[980px] text-left text-sm">
                    <thead className="bg-slate-100 text-slate-700">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Quotation Number</th>
                        <th className="px-4 py-3 font-semibold">Quotation Date</th>
                        <th className="px-4 py-3 font-semibold">Items</th>
                        <th className="px-4 py-3 font-semibold">Total Amount</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerQuotations.map((quotation) => (
                        <tr key={quotation._id} className="border-t border-slate-200">
                          <td className="px-4 py-3 font-medium text-slate-900">{quotation.quotationNo || "-"}</td>
                          <td className="px-4 py-3 text-slate-700">{formatDate(quotation.quotationDate)}</td>
                          <td className="px-4 py-3 text-slate-700">{getItemsSummary(quotation.items)}</td>
                          <td className="px-4 py-3 text-slate-700">Rs {getQuotationTotalAmount(quotation).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3">
                            {canManageCustomer || isAccountsView ? (
                              <select
                                value={quotation.status}
                                onChange={(e) => handleStatusChange(quotation._id, e.target.value)}
                                className={`rounded-full px-2.5 py-1 text-xs font-semibold border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-500 pr-8 ${getDocumentStatusStyles(quotation.status)}`}
                                style={{
                                  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23374151' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E")`,
                                  backgroundPosition: 'right 0.25rem center',
                                  backgroundSize: '1.25em 1.25em',
                                  backgroundRepeat: 'no-repeat',
                                  appearance: 'none',
                                  paddingRight: '1.5rem'
                                }}
                              >
                                <option value="draft" className="bg-white text-slate-900">Draft</option>
                                <option value="sent" className="bg-white text-slate-900">Sent</option>
                                <option value="accepted" className="bg-white text-slate-900">Accepted</option>
                                <option value="rejected" className="bg-white text-slate-900">Rejected</option>
                                <option value="expired" className="bg-white text-slate-900">Expired</option>
                                <option value="invoiced" className="bg-white text-slate-900">Invoiced</option>
                              </select>
                            ) : (
                              <StatusBadge label={formatDocumentStatus(quotation.status)} className={getDocumentStatusStyles(quotation.status)} />
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <ActionButton label="View" tone="sky" onClick={() => setViewingQuotation(quotation)} />
                              <ActionDropdown
                                label="Actions"
                                items={[
                                  {
                                    label: "Edit",
                                    icon: (
                                      <svg className="h-4 w-4 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    ),
                                    onClick: () => handleEditQuotation(quotation),
                                    hidden: !(canManageCustomer || isAccountsView)
                                  },
                                  {
                                    label: "PDF",
                                    icon: (
                                      <svg className="h-4 w-4 text-sky-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                      </svg>
                                    ),
                                    onClick: () => openQuotationPdf(quotation._id)
                                  },
                                  {
                                    label: "Re-Quotation",
                                    icon: (
                                      <svg className="h-4 w-4 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                      </svg>
                                    ),
                                    onClick: () => handleReQuotation(quotation),
                                    hidden: !(canManageCustomer || isAccountsView)
                                  }
                                ]}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Quoted Products</h2>
                  <p className="mt-1 text-sm text-slate-500">List of all items ever quoted for this customer.</p>
                </div>
              </div>

              {/* Filters Block */}
              <div className="grid gap-4 md:grid-cols-4 items-end mb-6 bg-slate-50 p-4 border border-slate-200 rounded-3xl">
                {/* Search Input */}
                <div className="flex flex-col gap-1 md:col-span-2">
                  <label className="text-xs font-bold text-slate-600">Search Products</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder="Search by Product Name, Item Code, HSN, Quotation No..."
                      value={quotedProductSearch}
                      onChange={(e) => setQuotedProductSearch(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-4 text-xs text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white"
                    />
                  </div>
                </div>

                {/* Date From */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">From Date</label>
                  <input
                    type="date"
                    value={quotedProductDateFrom}
                    onChange={(e) => setQuotedProductDateFrom(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 outline-none transition focus:border-sky-400"
                  />
                </div>

                {/* Date To */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">To Date</label>
                  <input
                    type="date"
                    value={quotedProductDateTo}
                    onChange={(e) => setQuotedProductDateTo(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 outline-none transition focus:border-sky-400"
                  />
                </div>

                {/* Quotation No filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Filter by Quotation No</label>
                  <input
                    type="text"
                    placeholder="e.g. GS/AI/2605-1"
                    value={quotedProductQuotationNo}
                    onChange={(e) => setQuotedProductQuotationNo(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 outline-none transition focus:border-sky-400"
                  />
                </div>
                
                {/* Clear Button */}
                {(quotedProductSearch || quotedProductDateFrom || quotedProductDateTo || quotedProductQuotationNo) && (
                  <div className="md:col-span-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setQuotedProductSearch("");
                        setQuotedProductDateFrom("");
                        setQuotedProductDateTo("");
                        setQuotedProductQuotationNo("");
                      }}
                      className="text-xs font-bold text-red-500 hover:text-red-700"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>

              {/* Products Table */}
              <div className="mt-6 overflow-x-auto">
                {filteredQuotedProducts.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
                    No quoted products found matching the criteria.
                  </div>
                ) : (
                  <table className="w-full min-w-[1100px] text-left text-sm">
                    <thead className="bg-slate-100 text-slate-700">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Quotation Date</th>
                        <th className="px-4 py-3 font-semibold">Quotation Number</th>
                        <th className="px-4 py-3 font-semibold">Product Name</th>
                        <th className="px-4 py-3 font-semibold">Item Code</th>
                        <th className="px-4 py-3 font-semibold">HSN Code</th>
                        <th className="px-4 py-3 font-semibold text-right">Quantity</th>
                        <th className="px-4 py-3">Unit</th>
                        <th className="px-4 py-3 text-right">Unit Price</th>
                        <th className="px-4 py-3 text-right">Total Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredQuotedProducts.map((p, idx) => (
                        <tr key={`${p.quotationId}-${idx}`} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3 text-slate-700">{formatDate(p.quotationDate)}</td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => setViewingQuotation(p.originalQuotation)}
                              className="font-medium text-blue-600 hover:underline hover:text-blue-800 text-left"
                            >
                              {p.quotationNo}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-slate-900 font-medium whitespace-pre-wrap">{p.productName}</td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-600">{p.itemCode || "-"}</td>
                          <td className="px-4 py-3 text-slate-500">{p.hsnCode || "-"}</td>
                          <td className="px-4 py-3 text-right text-slate-900 font-semibold">{p.quantity}</td>
                          <td className="px-4 py-3 text-slate-600">{p.unit || "No"}</td>
                          <td className="px-4 py-3 text-right text-slate-700">₹{(p.unitPrice || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 text-right text-slate-950 font-bold">₹{(p.totalPrice || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {activeSection === "sales-invoices" && (
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Sales Invoice</h2>
              <p className="mt-1 text-sm text-slate-500">Only sales invoices created for this customer are shown here.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="rounded-full bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700">
                {groupedCustomerSalesInvoices.length} Invoices
              </div>
              <div className="rounded-full bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700">
                Balance Pending: Rs {customerDocumentSummary.balancePending.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          <div className="mt-6 overflow-x-auto">
            {groupedCustomerSalesInvoices.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
                No sales invoices found for this customer yet.
              </div>
            ) : (
              <table className="w-full min-w-[1180px] text-left text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Invoice Number</th>
                    <th className="px-4 py-3 font-semibold">Invoice Date</th>
                    <th className="px-4 py-3 font-semibold">Customer Name</th>
                    <th className="px-4 py-3 font-semibold">Total Invoice Amount</th>
                    <th className="px-4 py-3 font-semibold">Amount Paid</th>
                    <th className="px-4 py-3 font-semibold">Balance Pending</th>
                    <th className="px-4 py-3 font-semibold">Payment Status</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedCustomerSalesInvoices.map((invoice) => {
                    const totalAmount = getSalesInvoiceTotalAmount(invoice);
                    const amountPaid = getSalesInvoiceAmountPaid(invoice);
                    const balancePending = getSalesInvoiceBalancePending(invoice);
                    const paymentStatus = formatPaymentStatusLabel(invoice);

                    return (
                      <tr key={invoice._id} className="border-t border-slate-200">
                        <td className="px-4 py-3 font-medium text-slate-900">{invoice.documentNumber || "-"}</td>
                        <td className="px-4 py-3 text-slate-700">{formatDate(invoice.transactionDate)}</td>
                        <td className="px-4 py-3 text-slate-700">{invoice.counterpartyName || customer.name || "-"}</td>
                        <td className="px-4 py-3 text-slate-700">Rs {totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-slate-700">Rs {amountPaid.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-slate-700">Rs {balancePending.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3">
                          <StatusBadge label={paymentStatus} className={getDocumentStatusStyles(paymentStatus)} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            <ActionButton label="View" tone="sky" onClick={() => openSalesInvoicePdf(invoice._id)} />
                            {(canManageCustomer || isAccountsView) && (
                              <ActionButton label="Edit" tone="amber" onClick={() => handleEditSalesInvoice(invoice)} />
                            )}
                            {(canManageCustomer || isAccountsView) && (
                              <ActionButton label="Delete" tone="red" onClick={() => handleDeleteSalesInvoice(invoice)} />
                            )}
                            {(canManageCustomer || isAccountsView) && (
                              <ActionButton label="Record Payment" tone="emerald" onClick={() => handleRecordPayment(invoice)} />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {isAccountsView && activeSection === "sales" && (
        <section className="rounded-[28px] border border-amber-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Sales</h2>
            </div>
            <button
              type="button"
              onClick={() => setShowSupplyForm((current) => !current)}
              className="rounded-2xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
            >
              {showSupplyForm ? "Hide Sales Form" : "Add Sales"}
            </button>
          </div>

          {showSupplyForm && (
            <form onSubmit={handleSupplySubmit} className="mt-6 rounded-[24px] border border-amber-100 bg-amber-50/50 p-5">
              <div className="grid gap-4 md:grid-cols-2">
                <SupplyField label="Material Name" name="itemName" value={supplyForm.itemName} onChange={handleSupplyFormChange} required />
                <SupplyField label="SKU / Code" name="sku" value={supplyForm.sku} onChange={handleSupplyFormChange} />
                <SupplyField label="Quantity" name="quantity" value={supplyForm.quantity} onChange={handleSupplyFormChange} type="number" min="0.01" step="0.01" required />
                <SupplyField label="Unit" name="unit" value={supplyForm.unit} onChange={handleSupplyFormChange} />
                <SupplyField label="Location / Store" name="location" value={supplyForm.location} onChange={handleSupplyFormChange} />
                <SupplyField label="Reference / DC No" name="reference" value={supplyForm.reference} onChange={handleSupplyFormChange} />
                <SupplyField label="Supply Date" name="transactionDate" value={supplyForm.transactionDate} onChange={handleSupplyFormChange} type="date" />
              </div>

              <div className="mt-4">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Notes</label>
                <textarea
                  name="notes"
                  value={supplyForm.notes}
                  onChange={handleSupplyFormChange}
                  rows={4}
                  className="w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-amber-400"
                  placeholder="Optional notes about the supplied material"
                />
              </div>

              <div className="mt-5 flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowSupplyForm(false)}
                  className="rounded-2xl bg-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={supplySaving}
                  className="rounded-2xl bg-amber-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-amber-600 disabled:opacity-60"
                >
                  {supplySaving ? "Saving..." : "Save Material Supply"}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 overflow-x-auto">
            {materialEntries.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
                No material supply recorded for this customer yet.
              </div>
            ) : (
              <table className="w-full min-w-[1000px] text-left text-sm">
                <thead className="border-b bg-gray-100">
                  <tr>
                    <th className="p-3 text-sm font-semibold text-slate-600">Date</th>
                    <th className="p-3 text-sm font-semibold text-slate-600">Material</th>
                    <th className="p-3 text-sm font-semibold text-slate-600">Quantity</th>
                    <th className="p-3 text-sm font-semibold text-slate-600">Location</th>
                    <th className="p-3 text-sm font-semibold text-slate-600">Reference</th>
                    <th className="p-3 text-sm font-semibold text-slate-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {materialEntries.map((entry) => (
                    <tr key={entry._id} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-slate-700">{formatDate(entry.transactionDate)}</td>
                      <td className="p-3">
                        <p className="font-medium text-slate-900">{entry.itemName || "-"}</p>
                        <p className="text-xs text-slate-500">{entry.sku || "No SKU"}</p>
                      </td>
                      <td className="p-3 text-slate-700">{entry.quantity} {formatUnit(entry.unit)}</td>
                      <td className="p-3 text-slate-700">{entry.location || "-"}</td>
                      <td className="p-3 text-slate-700">{entry.reference || "-"}</td>
                      <td className="p-3 text-slate-700">{entry.notes || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {activeSection === "earth-pit" && (
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Earth Pit</h2>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {earthPitEntries.length > 0 && (
                <div className="rounded-full bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700">
                  {earthPitEntries.length} Records
                </div>
              )}
              {earthPitEntries.length > 0 && (
                <input
                  type="date"
                  value={earthPitFilterDate}
                  onChange={(event) => setEarthPitFilterDate(event.target.value)}
                  className="rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
                />
              )}
              {earthPitEntries.length > 0 && (
                <select
                  value={earthPitSort}
                  onChange={(event) => setEarthPitSort(event.target.value)}
                  className="rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
                >
                  <option value="testedOn-desc">Latest Tested On</option>
                  <option value="testedOn-asc">Oldest Tested On</option>
                  <option value="earthPitNo-asc">Earth Pit No A-Z</option>
                  <option value="earthPitNo-desc">Earth Pit No Z-A</option>
                </select>
              )}
              {earthPitEntries.length > 0 && (
                <button
                  type="button"
                  onClick={openEarthPitPdf}
                  className="rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-300 transition hover:bg-slate-50"
                >
                  Open PDF
                </button>
              )}
              {earthPitEntries.length > 0 && (
                <button
                  type="button"
                  onClick={generateEarthPitPdf}
                  className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  Generate PDF
                </button>
              )}
            </div>
          </div>

          {showEarthPitForm && (
            <form onSubmit={handleEarthPitSubmit} className="mt-5 rounded-3xl border border-sky-100 bg-sky-50 p-5">
              <div className="mb-4 flex justify-end">
                <button
                  type="button"
                  onClick={addEarthPitEntryRow}
                  className="rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
                >
                  + Add Earth Pit
                </button>
              </div>

              <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
                Specified Value: Individual - 5 Ohms, Combined - 2 Ohms
              </div>

              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <SupplyField
                  label="Tested On"
                  name="testedOn"
                  type="date"
                  value={earthPitForm.testedOn}
                  onChange={handleEarthPitCommonChange}
                  required
                />
                <SupplyField
                  label="Due Date"
                  name="dueDate"
                  type="date"
                  value={earthPitForm.dueDate}
                  readOnly
                />
              </div>

              <div className="overflow-x-auto rounded-3xl border border-sky-200 bg-white">
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead className="bg-slate-100 text-slate-700">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Earth Pit No</th>
                      <th className="px-4 py-3 font-semibold">Individual Resistance (Ohm)</th>
                      <th className="px-4 py-3 font-semibold">Combined Resistance (Ohm)</th>
                      <th className="px-4 py-3 font-semibold">Additional Information</th>
                      <th className="px-4 py-3 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earthPitForm.entries.map((entry, index) => (
                      <tr key={`customer-earth-pit-entry-${index}`} className="border-t border-slate-200">
                        <td className="px-3 py-3">
                          <input
                            value={entry.earthPitNo}
                            onChange={(event) => handleEarthPitEntryChange(index, "earthPitNo", event.target.value)}
                            className="w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-amber-400"
                            placeholder="Earth pit no"
                            required
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            value={entry.individualResistance}
                            onChange={(event) => handleEarthPitEntryChange(index, "individualResistance", event.target.value)}
                            className="w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-amber-400"
                            placeholder="Individual resistance"
                            required
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            value={entry.combinedResistance}
                            onChange={(event) => handleEarthPitEntryChange(index, "combinedResistance", event.target.value)}
                            className="w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-amber-400"
                            placeholder="Combined resistance"
                            required
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            value={entry.additionalInfo || ""}
                            onChange={(event) => handleEarthPitEntryChange(index, "additionalInfo", event.target.value)}
                            className="w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-amber-400"
                            placeholder="Additional information"
                          />
                        </td>
                        <td className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => removeEarthPitEntryRow(index)}
                            disabled={earthPitForm.entries.length === 1}
                            className="rounded-2xl bg-red-100 px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-5 flex justify-end">
                <button
                  type="submit"
                  disabled={earthPitSaving}
                  className="rounded-2xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {earthPitSaving ? "Saving..." : "Save Earth Pit"}
                </button>
              </div>
            </form>
          )}

          {earthPitEntries.length > 0 && (
            <div className="mt-5 overflow-x-auto rounded-3xl border border-slate-200">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Earth Pit No</th>
                    <th className="px-4 py-3 font-semibold">Individual Resistance (Ohm)</th>
                    <th className="px-4 py-3 font-semibold">Combined Resistance (Ohm)</th>
                    <th className="px-4 py-3 font-semibold">Additional Information</th>
                    <th className="px-4 py-3 font-semibold">Tested On</th>
                    <th className="px-4 py-3 font-semibold">Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEarthPitEntries.map((entry) => (
                    <tr key={entry._id} className="border-t border-slate-200">
                      <td className="px-4 py-3 font-medium text-slate-900">{entry.earthPitNo || "-"}</td>
                      <td className="px-4 py-3 text-slate-700">{entry.individualResistance || entry.earthResistance || "-"}</td>
                      <td className="px-4 py-3 text-slate-700">{entry.combinedResistance || "-"}</td>
                      <td className="px-4 py-3 text-slate-700">{entry.additionalInfo || "-"}</td>
                      <td className="px-4 py-3 text-slate-700">{formatDisplayDate(entry.testedOn)}</td>
                      <td className="px-4 py-3 text-slate-700">{formatDisplayDate(entry.dueDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {activeSection === "complaints-reports" && (
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          {/* Sub-tab Switcher Bar for Complaints & Reports */}
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3 mb-6">
            <button
              type="button"
              onClick={() => setComplaintsReportsSubTab("complaints-reports")}
              className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
                complaintsReportsSubTab === "complaints-reports"
                  ? "bg-sky-600 text-white shadow-sm border-sky-600"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Complaints &amp; Report
            </button>
            <button
              type="button"
              onClick={() => setComplaintsReportsSubTab("products")}
              className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
                complaintsReportsSubTab === "products"
                  ? "bg-sky-600 text-white shadow-sm border-sky-600"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Products ({effectiveCustomerProducts.length})
            </button>
            <button
              type="button"
              onClick={() => setComplaintsReportsSubTab("components-to-replace")}
              className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
                complaintsReportsSubTab === "components-to-replace"
                  ? "bg-sky-600 text-white shadow-sm border-sky-600"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Components to be Replaced ({replacementComponents.length})
            </button>
            <button
              type="button"
              onClick={() => setComplaintsReportsSubTab("components-replaced")}
              className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
                complaintsReportsSubTab === "components-replaced"
                  ? "bg-emerald-600 text-white shadow-sm border-emerald-600"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Components Replaced ({replacedComponents.length})
            </button>
          </div>

          {/* Sub-tab 1: Complaints & Report History */}
          {complaintsReportsSubTab === "complaints-reports" && (
            <div>
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Complaint &amp; Report History</h2>
                </div>
              </div>

              {historyRows.length === 0 ? (
                <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
                  No complaints or reports found for this customer yet.
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  {historyRows.map((row) => (
                    <div
                      key={row.complaintDbId}
                      className="rounded-[26px] border border-slate-200 bg-[linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(239,246,255,0.72))] p-5"
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-lg font-semibold text-slate-900">{row.complaintId}</p>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyles(row.finalStatus)}`}>
                              {normalizeStatus(row.finalStatus)}
                            </span>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyles(row.complaintStatus)}`}>
                              Complaint: {normalizeStatus(row.complaintStatus)}
                            </span>
                          </div>

                          <p className="max-w-3xl text-sm leading-6 text-slate-600">{row.issue}</p>

                          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                            <HistoryField label="Engineer" value={row.engineer} />
                            <HistoryField label="Report ID" value={row.reportId} />
                            <HistoryField label="Visit Date" value={formatDate(row.visitDate)} />
                            <HistoryField label="Updated" value={formatDate(row.updatedAt)} />
                          </div>

                          {/* Replaced Components & Components to be Replaced Badges */}
                          {row.replacedComponents && row.replacedComponents.length > 0 && (
                            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                                <span className="material-symbols-outlined text-[13px]">check_circle</span>
                                Replaced:
                              </span>
                              {row.replacedComponents.map((comp, cIdx) => (
                                <span
                                  key={cIdx}
                                  className="inline-flex items-center gap-1 rounded-xl bg-emerald-100/70 border border-emerald-200 px-2.5 py-0.5 text-xs font-semibold text-emerald-800"
                                >
                                  <span>{comp.name}</span>
                                  {comp.quantity > 1 && (
                                    <span className="rounded-full bg-emerald-600 px-1.5 py-0.2 text-[10px] text-white">
                                      ×{comp.quantity}
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                          )}

                          {row.componentsToBeReplaced && row.componentsToBeReplaced.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                                <span className="material-symbols-outlined text-[13px]">build</span>
                                To Be Replaced:
                              </span>
                              {row.componentsToBeReplaced.map((comp, cIdx) => (
                                <span
                                  key={cIdx}
                                  className="inline-flex items-center gap-1 rounded-xl bg-rose-100/70 border border-rose-200 px-2.5 py-0.5 text-xs font-semibold text-rose-800"
                                >
                                  <span>{comp.name}</span>
                                  {comp.quantity > 1 && (
                                    <span className="rounded-full bg-rose-600 px-1.5 py-0.2 text-[10px] text-white">
                                      ×{comp.quantity}
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2 lg:justify-end">
                          {row.visitId ? (
                            <>
                              {canManageCustomer && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate("/create-complaint", {
                                      state: {
                                        prefill: {
                                          customerId: row.customerId,
                                          productType: row.productType,
                                          nature: row.issue,
                                          issue: row.issue,
                                          engineerAssigned: row.engineerAssigned,
                                          visitDate: "",
                                          contactName: row.contactName,
                                          contactPhone: row.contactPhone
                                        }
                                      }
                                    })
                                  }
                                  className="rounded-2xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700"
                                >
                                  Re-Visit
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => navigate(
                                  canManageCustomer
                                    ? `/reports/${row.visitId}`
                                    : isAccountsView
                                    ? `/accounts/reports/${row.visitId}`
                                    : `/my-reports/${row.visitId}`
                                )}
                                className="rounded-2xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700"
                              >
                                View Report
                              </button>

                              <button
                                type="button"
                                onClick={() => openReportPdf(row.visitId)}
                                className="rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
                              >
                                Open PDF
                              </button>

                              <button
                                type="button"
                                onClick={() => openReportEmailDraft(row)}
                                className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                              >
                                Send Email
                              </button>
                            </>
                          ) : (
                            <span className="rounded-2xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-500">
                              Report not submitted yet
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sub-tab 2: Products */}
          {complaintsReportsSubTab === "products" && (
            <div>
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Products</h2>
                  <p className="mt-1 text-sm text-slate-500">List of all products and assets serviced in complaints &amp; service reports for this customer.</p>
                </div>
                <div className="rounded-full bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700">
                  {filteredCustomerProducts.length} showing of {effectiveCustomerProducts.length} total
                </div>
              </div>

              {/* Filters and Sorting Block */}
              <div className="grid gap-4 md:grid-cols-4 items-end mb-6 bg-slate-50 p-4 border border-slate-200 rounded-3xl">
                {/* Search Input */}
                <div className="flex flex-col gap-1 md:col-span-1">
                  <label className="text-xs font-bold text-slate-600">Search Products</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder="Search name, serial, feeder..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-4 text-xs text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white"
                    />
                  </div>
                </div>

                {/* Product Type Filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Product Type</label>
                  <select
                    value={productTypeFilter}
                    onChange={(e) => setProductTypeFilter(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition focus:border-sky-400"
                  >
                    <option value="All">All Breakers</option>
                    <option value="acb">ACB (Air Circuit Breaker)</option>
                    <option value="mccb">MCCB (Molded Case Breaker)</option>
                    <option value="vcb">VCB (Vacuum Circuit Breaker)</option>
                    <option value="other">Other Products</option>
                  </select>
                </div>

                {/* Panel Area Filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Panel Area</label>
                  <input
                    type="text"
                    placeholder="e.g. Substation, Panel..."
                    value={productPanelAreaFilter}
                    onChange={(e) => setProductPanelAreaFilter(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition focus:border-sky-400"
                  />
                </div>

                {/* Sorting */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Sort By</label>
                  <select
                    value={productSortBy}
                    onChange={(e) => setProductSortBy(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition focus:border-sky-400"
                  >
                    <option value="serviceDate-desc">Latest Service Date</option>
                    <option value="serviceDate-asc">Oldest Service Date</option>
                    <option value="dueDate-asc">Next Service Due Date</option>
                    <option value="productName-asc">Product Name (A-Z)</option>
                    <option value="panelArea-asc">Panel Area (A-Z)</option>
                  </select>
                </div>

                {/* Clear Filters */}
                {(productSearch || productTypeFilter !== "All" || productPanelAreaFilter || productSortBy !== "serviceDate-desc") && (
                  <div className="md:col-span-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setProductSearch("");
                        setProductTypeFilter("All");
                        setProductPanelAreaFilter("");
                        setProductSortBy("serviceDate-desc");
                      }}
                      className="text-xs font-bold text-red-500 hover:text-red-700"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-6 overflow-x-auto">
                {filteredCustomerProducts.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
                    No products match the selected criteria.
                  </div>
                ) : (
                  <table className="w-full min-w-[1000px] text-left text-sm">
                    <thead className="bg-slate-100 text-slate-700">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Product Name</th>
                        <th className="px-4 py-3 font-semibold">Serial Number</th>
                        <th className="px-4 py-3 font-semibold">Feeder Name</th>
                        <th className="px-4 py-3 font-semibold">Panel Area</th>
                        <th className="px-4 py-3 font-semibold text-center">Service Date</th>
                        <th className="px-4 py-3 font-semibold text-center">Next Service Due</th>
                        <th className="px-4 py-3 font-semibold">Components to be Replaced</th>
                        <th className="px-4 py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredCustomerProducts.map((pRecord, idx) => {
                        const prod = pRecord.product || {};
                        const pName = prod.productName || prod.product || "-";
                        const serviceDate = pRecord.visit.visitDate || "";
                        const dueDate = calculateDueDate(serviceDate);

                        const toBeReplacedComps = (() => {
                          const comps = [];
                          const seen = new Set();

                          if (Array.isArray(prod.componentsToBeReplaced)) {
                            prod.componentsToBeReplaced.forEach((c) => {
                              if (!c) return;
                              const status = String(c.status || "").trim().toLowerCase();
                              if (status === "replaced") return;
                              const raw = String(c.name || c.componentName || (typeof c === "string" ? c : "")).trim();
                              if (!raw) return;
                              const qty = c.quantity ? Number(c.quantity) : 0;
                              const label = qty > 1 ? `${raw} (${qty})` : raw;
                              const key = raw.toLowerCase();
                              if (!seen.has(key)) {
                                seen.add(key);
                                comps.push(label);
                              }
                            });
                          }

                          if (Array.isArray(prod.compReplace)) {
                            prod.compReplace.forEach((c) => {
                              if (!c) return;
                              String(c)
                                .split(/,|;|\n/gi)
                                .map((s) => s.trim())
                                .filter(Boolean)
                                .forEach((part) => {
                                  const key = part.toLowerCase();
                                  if (!seen.has(key)) {
                                    seen.add(key);
                                    comps.push(part);
                                  }
                                });
                            });
                          } else if (typeof prod.compReplace === "string" && prod.compReplace.trim()) {
                            String(prod.compReplace)
                              .split(/,|;|\n/gi)
                              .map((s) => s.trim())
                              .filter(Boolean)
                              .forEach((part) => {
                                const key = part.toLowerCase();
                                if (!seen.has(key)) {
                                  seen.add(key);
                                  comps.push(part);
                                }
                              });
                          }

                          if (comps.length === 0) {
                            const statusText = String(prod.serviceStatus || prod.status || "").trim();
                            if (statusText && /to be replace|replace|defective|damaged|faulty/i.test(statusText) && !/satisfactor|ok|good|working/i.test(statusText)) {
                              comps.push(statusText);
                            }
                          }

                          return comps;
                        })();

                        return (
                          <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3 text-slate-900 text-xs font-medium whitespace-pre-wrap leading-tight">{pName}</td>
                            <td className="px-4 py-3 font-mono text-xs text-slate-700 font-semibold">{prod.serialNo || "N/A"}</td>
                            <td className="px-4 py-3 text-slate-700 text-xs font-medium">{prod.feeder || prod.feederName || "-"}</td>
                            <td className="px-4 py-3 text-slate-700">{prod.panelArea || "-"}</td>
                            <td className="px-4 py-3 text-center text-slate-600">{formatDisplayDate(serviceDate)}</td>
                            <td className="px-4 py-3 text-center text-slate-600 font-semibold">{dueDate}</td>
                            <td className="px-4 py-3">
                              {toBeReplacedComps.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {toBeReplacedComps.map((comp, cIdx) => (
                                    <span
                                      key={cIdx}
                                      className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800"
                                    >
                                      {comp}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400 italic">None</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => setViewingProduct(pRecord)}
                                className="rounded-2xl bg-sky-600 hover:bg-sky-700 text-white px-3.5 py-1.5 text-xs font-semibold transition"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Sub-tab 3: Components to be Replaced */}
          {complaintsReportsSubTab === "components-to-replace" && (
            <div>
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Components to be Replaced</h2>
                  <p className="mt-1 text-sm text-slate-500">List of all defective components identified for replacement in complaints &amp; service reports.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="rounded-full bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700">
                    {filteredReplacementComponents.length} showing of {replacementComponents.length} total
                  </div>
                  <button
                    type="button"
                    onClick={handleExportReplacementComponentsExcel}
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs font-bold transition shadow-sm hover:shadow"
                    title="Export Components to be Replaced to Excel"
                  >
                    <span className="material-symbols-outlined text-base">download</span>
                    Export Excel
                  </button>
                </div>
              </div>

              {/* Filters and Sorting Block */}
              <div className="grid gap-4 md:grid-cols-3 items-end mb-6 bg-slate-50 p-4 border border-slate-200 rounded-3xl">
                {/* Search Input */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Search Components</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder="Search component name, product, serial..."
                      value={componentSearch}
                      onChange={(e) => setComponentSearch(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-4 text-xs text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white"
                    />
                  </div>
                </div>

                {/* Product Filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Filter by Product</label>
                  <select
                    value={componentProductFilter}
                    onChange={(e) => setComponentProductFilter(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition focus:border-sky-400"
                  >
                    <option value="All">All Products</option>
                    {uniqueReplacementProducts.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>

                {/* Sorting */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Sort By</label>
                  <select
                    value={componentSortBy}
                    onChange={(e) => setComponentSortBy(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition focus:border-sky-400"
                  >
                    <option value="visitDate-desc">Latest Service Date</option>
                    <option value="visitDate-asc">Oldest Service Date</option>
                    <option value="quantity-desc">Highest Quantity</option>
                    <option value="quantity-asc">Lowest Quantity</option>
                    <option value="name-asc">Component Name (A-Z)</option>
                  </select>
                </div>

                {/* Clear Filters */}
                {(componentSearch || componentProductFilter !== "All" || componentSortBy !== "visitDate-desc") && (
                  <div className="md:col-span-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setComponentSearch("");
                        setComponentProductFilter("All");
                        setComponentSortBy("visitDate-desc");
                      }}
                      className="text-xs font-bold text-red-500 hover:text-red-700"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>

              {filteredReplacementComponents.length > 0 && (
                <div className="mb-6 rounded-3xl border border-slate-100 bg-slate-50/50 p-5">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3.5 flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-sm text-slate-500">summarize</span>
                    Defective Components Summary (Aggregated Counts)
                  </h3>
                  <div className="flex flex-wrap gap-2.5">
                    {componentSummary.map(([name, qty]) => (
                      <div
                        key={name}
                        className="flex items-center gap-2 rounded-2xl border border-rose-100 bg-rose-50/50 px-3.5 py-1.5 text-xs font-semibold text-rose-800 shadow-sm transition hover:scale-105 hover:bg-rose-50 cursor-default"
                      >
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white shadow-sm">
                          {qty}
                        </span>
                        <span>{name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 overflow-x-auto">
                {filteredReplacementComponents.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
                    No replacement components match the selected criteria.
                  </div>
                ) : (
                  <table className="w-full min-w-[1000px] text-left text-sm">
                    <thead className="bg-slate-100 text-slate-700">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Defective Component</th>
                        <th className="px-4 py-3 font-semibold">Frame and Pole</th>
                        <th className="px-4 py-3 font-semibold text-center">No. of Items</th>
                        <th className="px-4 py-3 font-semibold">Product Name</th>
                        <th className="px-4 py-3 font-semibold">Serial Number</th>
                        <th className="px-4 py-3 font-semibold">Panel Area</th>
                        <th className="px-4 py-3 font-semibold">Original Description</th>
                        <th className="px-4 py-3 font-semibold text-center">Service Date</th>
                        <th className="px-4 py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredReplacementComponents.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3 text-slate-900 font-semibold">{item.componentName}</td>
                          <td className="px-4 py-3">
                            <span className="inline-block rounded-md bg-slate-100 border border-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">
                              {item.frameAndPole || "-"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-slate-900 font-bold">{item.quantity}</td>
                          <td className="px-4 py-3 text-slate-700 font-medium whitespace-pre-wrap">{item.productName}</td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-600 font-semibold">
                            {item.serialNo && item.serialNo !== "N/A" ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/serial/${encodeURIComponent(item.serialNo)}`)}
                                className="text-sky-600 hover:text-sky-800 underline decoration-dotted"
                                title="View serial product details"
                              >
                                {item.serialNo}
                              </button>
                            ) : (
                              item.serialNo || "-"
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{item.panelArea}</td>
                          <td className="px-4 py-3 text-slate-500 italic text-xs">&quot;{item.originalText}&quot;</td>
                          <td className="px-4 py-3 text-center text-slate-600">{formatDisplayDate(item.visitDate)}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => setViewingProduct(item.productRecord || {
                                  product: { productName: item.productName, serialNo: item.serialNo, panelArea: item.panelArea, category: item.category },
                                  visit: { _id: item.visitId, visitId: item.reportId, visitDate: item.visitDate }
                                })}
                                className="rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 text-xs font-semibold transition whitespace-nowrap shadow-sm"
                                title="View only that product details"
                              >
                                View Product
                              </button>
                              {item.visitId ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => navigate(
                                      canManageCustomer
                                        ? `/reports/${item.visitId}`
                                        : isAccountsView
                                        ? `/accounts/reports/${item.visitId}`
                                        : `/my-reports/${item.visitId}`
                                    )}
                                    className="rounded-2xl bg-sky-600 hover:bg-sky-700 text-white px-3 py-1.5 text-xs font-semibold transition whitespace-nowrap"
                                  >
                                    View Report
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openReportPdf(item.visitId)}
                                    className="rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-semibold transition whitespace-nowrap"
                                  >
                                    PDF
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 text-slate-800 font-bold border-t border-slate-200">
                      <tr>
                        <td className="px-4 py-3.5">Total Components</td>
                        <td></td>
                        <td className="px-4 py-3.5 text-center text-rose-700 font-extrabold text-base">
                          {filteredReplacementComponents.reduce((sum, item) => sum + item.quantity, 0)}
                        </td>
                        <td colSpan="6"></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Sub-tab 4: Components Replaced */}
          {complaintsReportsSubTab === "components-replaced" && (
            <div>
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Components Replaced</h2>
                  <p className="mt-1 text-sm text-slate-500">List of all components successfully replaced during complaints &amp; service report visits for this customer.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 border border-emerald-100">
                    {filteredReplacedComponents.length} showing of {replacedComponents.length} total
                  </div>
                  <button
                    type="button"
                    onClick={handleExportReplacedComponentsExcel}
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs font-bold transition shadow-sm hover:shadow"
                    title="Export Replaced Components to Excel"
                  >
                    <span className="material-symbols-outlined text-base">download</span>
                    Export Excel
                  </button>
                </div>
              </div>

              {/* Filters and Sorting Block */}
              <div className="grid gap-4 md:grid-cols-3 items-end mb-6 bg-slate-50 p-4 border border-slate-200 rounded-3xl">
                {/* Search Input */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Search Replaced Components</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder="Search component, product, serial, remarks..."
                      value={replacedComponentSearch}
                      onChange={(e) => setReplacedComponentSearch(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-4 text-xs text-slate-700 outline-none transition focus:border-emerald-400 focus:bg-white"
                    />
                  </div>
                </div>

                {/* Product Filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Filter by Product</label>
                  <select
                    value={replacedComponentProductFilter}
                    onChange={(e) => setReplacedComponentProductFilter(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition focus:border-emerald-400"
                  >
                    <option value="All">All Products</option>
                    {uniqueReplacedProducts.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>

                {/* Sorting */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Sort By</label>
                  <select
                    value={replacedComponentSortBy}
                    onChange={(e) => setReplacedComponentSortBy(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition focus:border-emerald-400"
                  >
                    <option value="visitDate-desc">Latest Replacement Date</option>
                    <option value="visitDate-asc">Oldest Replacement Date</option>
                    <option value="quantity-desc">Highest Quantity</option>
                    <option value="quantity-asc">Lowest Quantity</option>
                    <option value="name-asc">Component Name (A-Z)</option>
                  </select>
                </div>

                {/* Clear Filters */}
                {(replacedComponentSearch || replacedComponentProductFilter !== "All" || replacedComponentSortBy !== "visitDate-desc") && (
                  <div className="md:col-span-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setReplacedComponentSearch("");
                        setReplacedComponentProductFilter("All");
                        setReplacedComponentSortBy("visitDate-desc");
                      }}
                      className="text-xs font-bold text-red-500 hover:text-red-700"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>

              {/* Aggregated Counts Summary */}
              {filteredReplacedComponents.length > 0 && (
                <div className="mb-6 rounded-3xl border border-slate-100 bg-slate-50/50 p-5">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3.5 flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-sm text-emerald-600">verified</span>
                    Replaced Components Summary (Aggregated Counts)
                  </h3>
                  <div className="flex flex-wrap gap-2.5">
                    {replacedComponentSummary.map(([name, qty]) => (
                      <div
                        key={name}
                        className="flex items-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50/60 px-3.5 py-1.5 text-xs font-semibold text-emerald-800 shadow-sm transition hover:scale-105 hover:bg-emerald-50 cursor-default"
                      >
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white shadow-sm">
                          {qty}
                        </span>
                        <span>{name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Replaced Components Table */}
              <div className="mt-6 overflow-x-auto">
                {filteredReplacedComponents.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
                    No replaced components match the selected criteria.
                  </div>
                ) : (
                  <table className="w-full min-w-[1050px] text-left text-sm">
                    <thead className="bg-slate-100 text-slate-700">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Replaced Component</th>
                        <th className="px-4 py-3 font-semibold">Frame and Pole</th>
                        <th className="px-4 py-3 font-semibold text-center">No. of Items</th>
                        <th className="px-4 py-3 font-semibold">Product Name</th>
                        <th className="px-4 py-3 font-semibold">Serial Number</th>
                        <th className="px-4 py-3 font-semibold">Panel Area</th>
                        <th className="px-4 py-3 font-semibold">Part Info / Remarks</th>
                        <th className="px-4 py-3 font-semibold text-center">Replaced Date</th>
                        <th className="px-4 py-3 font-semibold">Engineer</th>
                        <th className="px-4 py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredReplacedComponents.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3 text-slate-900 font-semibold">
                            <div className="flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                              <span>{item.componentName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-block rounded-md bg-slate-100 border border-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">
                              {item.frameAndPole || "-"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-emerald-700 font-bold">{item.quantity}</td>
                          <td className="px-4 py-3 text-slate-700 font-medium whitespace-pre-wrap">{item.productName}</td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-600 font-semibold">
                            {item.serialNo && item.serialNo !== "N/A" ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/serial/${encodeURIComponent(item.serialNo)}`)}
                                className="text-sky-600 hover:text-sky-800 underline decoration-dotted"
                                title="View serial product details"
                              >
                                {item.serialNo}
                              </button>
                            ) : (
                              item.serialNo || "-"
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{item.panelArea}</td>
                          <td className="px-4 py-3 text-xs text-slate-600 max-w-[220px]">
                            {item.previousPart || item.newPart ? (
                              <div>
                                {item.previousPart && (
                                  <p className="text-slate-500">Old: <span className="font-medium text-slate-700">{item.previousPart}</span></p>
                                )}
                                {item.newPart && (
                                  <p className="text-emerald-700 font-medium">New: {item.newPart}</p>
                                )}
                                {item.remarks && <p className="italic text-slate-500 mt-0.5">{item.remarks}</p>}
                              </div>
                            ) : item.remarks ? (
                              <span className="italic text-slate-500">{item.remarks}</span>
                            ) : (
                              <span className="italic text-slate-400 text-xs">&quot;{item.originalText}&quot;</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-slate-600 whitespace-nowrap">
                            {formatDisplayDate(item.replacementDate || item.visitDate)}
                          </td>
                          <td className="px-4 py-3 text-slate-700 text-xs font-medium">
                            {item.engineer || "-"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => setViewingProduct(item.productRecord || {
                                  product: { productName: item.productName, serialNo: item.serialNo, panelArea: item.panelArea, category: item.category },
                                  visit: { _id: item.visitId, visitId: item.reportId, visitDate: item.visitDate }
                                })}
                                className="rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 text-xs font-semibold transition whitespace-nowrap shadow-sm"
                                title="View only that product details"
                              >
                                View Product
                              </button>
                              {item.visitId ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => navigate(
                                      canManageCustomer
                                        ? `/reports/${item.visitId}`
                                        : isAccountsView
                                        ? `/accounts/reports/${item.visitId}`
                                        : `/my-reports/${item.visitId}`
                                    )}
                                    className="rounded-2xl bg-sky-600 hover:bg-sky-700 text-white px-3 py-1.5 text-xs font-semibold transition whitespace-nowrap"
                                  >
                                    View Report
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openReportPdf(item.visitId)}
                                    className="rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-semibold transition whitespace-nowrap"
                                  >
                                    PDF
                                  </button>
                                </>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 text-slate-800 font-bold border-t border-slate-200">
                      <tr>
                        <td className="px-4 py-3.5">Total Components Replaced</td>
                        <td></td>
                        <td className="px-4 py-3.5 text-center text-emerald-700 font-extrabold text-base">
                          {filteredReplacedComponents.reduce((sum, item) => sum + item.quantity, 0)}
                        </td>
                        <td colSpan="7"></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>
          )}
        </section>
      )}


      {showHistoryModal && (
        <CustomerHistoryModal
          historyRows={historyRows}
          onClose={() => setShowHistoryModal(false)}
          onSendEmail={openReportEmailDraft}
          onViewReport={(visitId) =>
            navigate(
              canManageCustomer
                ? `/reports/${visitId}`
                : isAccountsView
                ? `/accounts/reports/${visitId}`
                : `/my-reports/${visitId}`
            )
          }
        />
      )}

      {emailDraft && (
        <EmailComposerModal
          draft={emailDraft}
          sending={sendingEmail}
          onChange={setEmailDraft}
          onClose={() => setEmailDraft(null)}
          onSend={sendReportEmail}
        />
      )}

      {contactModalOpen && (
        <ContactEditorModal
          contact={contactForm}
          saving={contactSaving}
          onChange={setContactForm}
          onClose={closeContactModal}
          onSave={saveContact}
        />
      )}

      {viewingQuotation && (
        <QuotationViewerModal
          quotation={viewingQuotation}
          onClose={() => setViewingQuotation(null)}
          onDuplicate={handleDuplicateQuotation}
          onPdf={() => openQuotationPdf(viewingQuotation._id)}
          onEdit={(canManageCustomer || isAccountsView) ? () => {
            handleEditQuotation(viewingQuotation);
            setViewingQuotation(null);
          } : null}
          onReQuotation={(canManageCustomer || isAccountsView) ? () => {
            handleReQuotation(viewingQuotation);
            setViewingQuotation(null);
          } : null}
        />
      )}

      {viewingProduct && (
        <ProductViewerModal
          productRecord={viewingProduct}
          onClose={() => setViewingProduct(null)}
          onViewReport={(visitId) => {
            setViewingProduct(null);
            navigate(
              canManageCustomer
                ? `/reports/${visitId}`
                : isAccountsView
                ? `/accounts/reports/${visitId}`
                : `/my-reports/${visitId}`
            );
          }}
        />
      )}

    </div>
  );
}

function ProductViewerModal({ productRecord, onClose, onViewReport }) {
  if (!productRecord) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="relative w-full max-w-4xl rounded-[28px] border border-slate-200 bg-white p-6 shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-100 pb-4 shrink-0">
          <div>
            <h2 className="text-xl font-bold text-slate-900 font-sans">Serviced Product Details</h2>
            <p className="mt-1 text-sm text-slate-500 font-semibold font-mono">
              Serial No: {productRecord.product?.serialNo || "N/A"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
            style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="mt-4 overflow-y-auto flex-1 pr-1">
          <SerialProductDetails
            product={productRecord.product}
            visit={productRecord.visit}
            complaint={productRecord.complaint}
            showVisitDetails={true}
            showQr={true}
          />
        </div>

        {/* Footer Actions */}
        <div className="mt-6 flex items-center justify-end gap-2 border-t border-slate-100 pt-4 shrink-0">
          {productRecord.visit?._id && (
            <button
              type="button"
              onClick={() => onViewReport(productRecord.visit._id)}
              className="rounded-[12px] bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
            >
              📄 View Service Report
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-[12px] border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2 text-xs font-semibold transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function QuotationViewerModal({ quotation, onClose, onDuplicate, onPdf, onEdit, onReQuotation }) {
  if (!quotation) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="relative w-full max-w-4xl rounded-[24px] border border-slate-200 bg-white p-6 shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-100 pb-4 shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-900">Quotation Details</h2>
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                quotation.status === "accepted" || quotation.status === "invoiced" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                quotation.status === "sent" ? "bg-blue-50 text-blue-700 border-blue-200" :
                quotation.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" :
                quotation.status === "expired" ? "bg-amber-50 text-amber-700 border-amber-200" :
                "bg-slate-50 text-slate-600 border-slate-200"
              }`}>
                {quotation.status}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500 font-semibold">{quotation.quotationNo || "Draft Quotation"}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800 focus:outline-none"
            style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: "0" }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="mt-4 overflow-y-auto space-y-5 flex-1 pr-1">
          {/* Metadata Cards */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-[16px] border border-slate-100 bg-slate-50/50 p-4 space-y-2">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Customer Details</h3>
              <div className="text-sm space-y-1">
                <p className="font-bold text-slate-900">{quotation.customerName || "-"}</p>
                {quotation.contactPerson && <p className="text-slate-600"><span className="font-semibold text-slate-500">Contact:</span> {quotation.contactPerson}</p>}
                {quotation.phone && <p className="text-slate-600"><span className="font-semibold text-slate-500">Phone:</span> {quotation.phone}</p>}
                {quotation.email && <p className="text-slate-600"><span className="font-semibold text-slate-500">Email:</span> {quotation.email}</p>}
                {quotation.gstNumber && <p className="text-slate-600"><span className="font-semibold text-slate-500">GST:</span> {quotation.gstNumber}</p>}
                {quotation.billingAddress && <p className="text-slate-600 mt-1 whitespace-pre-wrap"><span className="font-semibold text-slate-500">Billing Address:</span><br/>{quotation.billingAddress}</p>}
              </div>
            </div>

            <div className="rounded-[16px] border border-slate-100 bg-slate-50/50 p-4 space-y-2">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Quotation Information</h3>
              <div className="text-sm space-y-1">
                <p className="text-slate-600"><span className="font-semibold text-slate-500">Date:</span> {quotation.quotationDate ? new Date(quotation.quotationDate).toLocaleDateString("en-GB") : "-"}</p>
                <p className="text-slate-600"><span className="font-semibold text-slate-500">Valid Until:</span> {quotation.validUntil ? new Date(quotation.validUntil).toLocaleDateString("en-GB") : "-"}</p>
                {quotation.referenceNo && <p className="text-slate-600"><span className="font-semibold text-slate-500">Reference No:</span> {quotation.referenceNo}</p>}
                {quotation.subject && <p className="text-slate-600"><span className="font-semibold text-slate-500">Subject:</span> {quotation.subject}</p>}
                {quotation.deliveryTime && <p className="text-slate-600"><span className="font-semibold text-slate-500">Delivery Time:</span> {quotation.deliveryTime}</p>}
                {quotation.paymentTerms && <p className="text-slate-600"><span className="font-semibold text-slate-500">Payment Terms:</span> {quotation.paymentTerms}</p>}
                {quotation.warranty && <p className="text-slate-600"><span className="font-semibold text-slate-500">Warranty:</span> {quotation.warranty}</p>}
                {(quotation.gstType === "sez_supply" || quotation.lutRefNo) && (
                  <p className="text-slate-600"><span className="font-semibold text-slate-500">LUT:</span> <span className="font-bold text-slate-800">TAX EXEMPTED</span>{quotation.lutRefNo ? ` • ${quotation.lutRefNo}` : ""}</p>
                )}
              </div>
            </div>
          </div>

          {/* Products / Items Table */}
          <div className="space-y-2">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1">
              <span>🛒</span> Quoted Products
            </h3>
            <div className="overflow-x-auto rounded-[12px] border border-slate-200 bg-white">
              <table className="w-full min-w-[700px] text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
                    <th className="p-2.5 text-center border-r border-slate-100 w-10">#</th>
                    <th className="p-2.5 border-r border-slate-100 w-28">Item Code</th>
                    <th className="p-2.5 border-r border-slate-100 w-24">HSN Code</th>
                    <th className="p-2.5 border-r border-slate-100">Description</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-16">Qty</th>
                    <th className="p-2.5 border-r border-slate-100 w-16">Unit</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">List Price</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">Discount</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">Unit Price</th>
                    <th className="p-2.5 text-right w-24">Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(quotation.items || []).map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50 align-middle">
                      <td className="p-2.5 text-center border-r border-slate-100 text-slate-400 font-semibold">{idx + 1}</td>
                      <td className="p-2.5 border-r border-slate-100 font-mono font-medium text-slate-600 uppercase">{item.itemCode || "-"}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-500">{item.hsnCode || "-"}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-800 font-medium whitespace-pre-wrap">{item.description}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-900 font-semibold">{item.quantity}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-600">{item.unit || "NA"}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-600 font-medium">₹{(item.listPrice ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-red-600 font-medium">
                        {item.discountValue ? (item.discountType === "amount" ? `₹${item.discountValue.toLocaleString("en-IN")}` : `${item.discountValue}%`) : "-"}
                      </td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-700 font-semibold">₹{(item.unitPrice ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="p-2.5 text-right text-slate-900 font-bold">₹{(item.totalAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                  {(!quotation.items || !quotation.items.length) && (
                    <tr>
                      <td colSpan="10" className="p-8 text-center text-slate-400">No products listed.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom Area: Terms & Summary */}
          <div className="grid gap-4 md:grid-cols-12">
            <div className="md:col-span-7 rounded-[16px] border border-slate-100 p-4 space-y-3">
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Terms & Remarks</h4>
              <div className="text-xs text-slate-600 space-y-2 leading-relaxed">
                {quotation.terms && <p><strong>Terms:</strong> {quotation.terms}</p>}
                {quotation.notes && <p><strong>Notes:</strong> {quotation.notes}</p>}
              </div>
            </div>

            <div className="md:col-span-5 rounded-[16px] border border-slate-200 bg-slate-50/50 p-4 space-y-2">
              <div className="flex justify-between text-xs font-bold text-slate-800">
                <span>Sub Total:</span>
                <span className="font-bold text-slate-900">₹{(quotation.subTotal ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-xs text-slate-500 font-semibold">
                <span>GST:</span>
                <span>₹{(quotation.gstAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-slate-800 border-t border-slate-200 pt-2">
                <span>Grand Total:</span>
                <span className="text-blue-600">₹{(quotation.grandTotal ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-6 flex items-center justify-end gap-2 border-t border-slate-100 pt-4 shrink-0">
          <button
            type="button"
            onClick={onPdf}
            className="rounded-[12px] bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
          >
            <span>📄</span> View PDF
          </button>
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="rounded-[12px] bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
            >
              <span>✏️</span> Edit
            </button>
          )}
          {onReQuotation && (
            <button
              type="button"
              onClick={onReQuotation}
              className="rounded-[12px] bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
            >
              <span>🔄</span> Re-Quotation
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              onDuplicate(quotation);
              onClose();
            }}
            className="rounded-[12px] bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
          >
            <span>👥</span> Duplicate to Other Customer
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[12px] border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2 text-xs font-semibold transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, tone }) {
  const tones = {
    blue: "from-sky-50 to-blue-100 text-sky-700",
    emerald: "from-emerald-50 to-green-100 text-emerald-700",
    amber: "from-amber-50 to-orange-100 text-amber-700",
    violet: "from-violet-50 to-fuchsia-100 text-violet-700"
  };

  return (
    <div className={`rounded-3xl border border-white/80 bg-gradient-to-br ${tones[tone] || tones.blue} px-5 py-4 shadow-sm`}>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-3 text-3xl font-bold">{value}</p>
    </div>
  );
}

function StatusBadge({ label, className }) {
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${className}`}>
      {label}
    </span>
  );
}

function ActionDropdown({ label = "Actions", items = [], align = "right" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const updateCoords = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const openUp = spaceBelow < 210 && spaceAbove > spaceBelow;

    let left = null;
    let right = null;

    if (align === "left") {
      left = Math.max(8, rect.left);
    } else {
      const rightDist = window.innerWidth - rect.right;
      if (rect.right < 210) {
        left = Math.max(8, rect.left);
      } else {
        right = Math.max(8, rightDist);
      }
    }

    if (openUp) {
      setCoords({
        top: null,
        bottom: Math.max(8, window.innerHeight - rect.top + 6),
        left,
        right,
        maxHeight: Math.min(360, Math.max(140, spaceAbove - 20))
      });
    } else {
      setCoords({
        top: Math.max(8, rect.bottom + 6),
        bottom: null,
        left,
        right,
        maxHeight: Math.min(360, Math.max(140, spaceBelow - 20))
      });
    }
  }, [align]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      updateCoords();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    updateCoords();

    const handleClickOutside = (event) => {
      if (
        (buttonRef.current && buttonRef.current.contains(event.target)) ||
        (menuRef.current && menuRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleScrollOrResize = (event) => {
      if (menuRef.current && menuRef.current.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, updateCoords]);

  const visibleItems = items.filter((item) => !item.hidden);
  if (visibleItems.length === 0) return null;

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:border-slate-400 focus:outline-none cursor-pointer"
      >
        <span>{label}</span>
        <span className={`text-[9px] text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
      </button>

      {isOpen && coords && createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            ...(coords.top !== null ? { top: `${coords.top}px` } : {}),
            ...(coords.bottom !== null ? { bottom: `${coords.bottom}px` } : {}),
            ...(coords.left !== null ? { left: `${coords.left}px` } : {}),
            ...(coords.right !== null ? { right: `${coords.right}px` } : {}),
            maxHeight: `${coords.maxHeight}px`,
            zIndex: 99999
          }}
          className="min-w-[170px] w-max max-w-[240px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl animate-fadeIn divide-y divide-slate-100"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-0.5 space-y-0.5">
            {visibleItems.map((item, idx) => (
              <button
                key={idx}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  item.onClick?.();
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center gap-2.5 cursor-pointer ${
                  item.danger
                    ? "text-rose-600 hover:bg-rose-50"
                    : item.highlight
                    ? "text-blue-700 hover:bg-blue-50"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {item.icon && <span className="text-sm shrink-0">{item.icon}</span>}
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function ActionButton({ label, tone, onClick }) {
  const tones = {
    sky: "bg-sky-600 hover:bg-sky-700 text-white",
    amber: "bg-amber-500 hover:bg-amber-600 text-white",
    emerald: "bg-emerald-600 hover:bg-emerald-700 text-white",
    red: "bg-red-600 hover:bg-red-700 text-white",
    violet: "bg-violet-600 hover:bg-violet-700 text-white"
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${tones[tone] || tones.sky}`}
    >
      {label}
    </button>
  );
}

function DetailCard({ label, value }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{label}</p>
      <div className="mt-2 text-sm leading-6 text-slate-700">{value}</div>
    </div>
  );
}

function HistoryField({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/80 px-4 py-3 ring-1 ring-slate-200">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <div className="mt-2 text-sm text-slate-700">{value || "-"}</div>
    </div>
  );
}

// ProductDetailModal was unused and has been removed to fix ESLint errors

function CustomerHistoryModal({ historyRows, onClose, onViewReport, onSendEmail }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative mx-4 max-h-[85vh] w-full max-w-5xl overflow-y-auto rounded-[28px] border border-slate-200 bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
        >
          ×
        </button>

        <h2 className="text-xl font-bold text-slate-900">Customer Service History</h2>

        {historyRows.length === 0 ? (
          <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
            No service history found.
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {historyRows.map((row) => (
              <div key={row.complaintDbId} className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-base font-semibold text-slate-900">{row.complaintId}</p>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyles(row.finalStatus)}`}>
                        {normalizeStatus(row.finalStatus)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{row.issue}</p>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                      <HistoryField label="Engineer" value={row.engineer} />
                      <HistoryField label="Report ID" value={row.reportId} />
                      <HistoryField label="Visit Date" value={formatDate(row.visitDate)} />
                      <HistoryField label="Updated" value={formatDate(row.updatedAt)} />
                    </div>

                    {/* Replaced Components & Components to be Replaced Badges */}
                    {row.replacedComponents && row.replacedComponents.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <span className="material-symbols-outlined text-[12px]">check_circle</span>
                          Replaced:
                        </span>
                        {row.replacedComponents.map((comp, cIdx) => (
                          <span
                            key={cIdx}
                            className="inline-flex items-center gap-1 rounded-xl bg-emerald-100/70 border border-emerald-200 px-2 py-0.5 text-xs font-semibold text-emerald-800"
                          >
                            <span>{comp.name}</span>
                            {comp.quantity > 1 && (
                              <span className="rounded-full bg-emerald-600 px-1.5 py-0.2 text-[9px] text-white">
                                ×{comp.quantity}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}

                    {row.componentsToBeReplaced && row.componentsToBeReplaced.length > 0 && (
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <span className="material-symbols-outlined text-[12px]">build</span>
                          To Be Replaced:
                        </span>
                        {row.componentsToBeReplaced.map((comp, cIdx) => (
                          <span
                            key={cIdx}
                            className="inline-flex items-center gap-1 rounded-xl bg-rose-100/70 border border-rose-200 px-2 py-0.5 text-xs font-semibold text-rose-800"
                          >
                            <span>{comp.name}</span>
                            {comp.quantity > 1 && (
                              <span className="rounded-full bg-rose-600 px-1.5 py-0.2 text-[9px] text-white">
                                ×{comp.quantity}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {row.visitId && (
                      <button
                        type="button"
                        onClick={() => onViewReport(row.visitId)}
                        className="rounded-2xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700"
                      >
                        View Report
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onSendEmail(row)}
                      className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                    >
                      Send Email
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmailComposerModal({ draft, sending, onChange, onClose, onSend }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative mx-4 w-full max-w-3xl rounded-[28px] border border-slate-200 bg-white p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
        >
          x
        </button>

        <div>
          <h2 className="text-xl font-bold text-slate-900">Send Report Via Email</h2>
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">To</label>
            <input
              type="email"
              value={draft.to}
              onChange={(event) => onChange((current) => ({ ...current, to: event.target.value }))}
              placeholder="customer@example.com"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">Subject</label>
            <input
              type="text"
              value={draft.subject}
              onChange={(event) => onChange((current) => ({ ...current, subject: event.target.value }))}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">Message</label>
            <textarea
              rows={16}
              value={draft.body}
              onChange={(event) => onChange((current) => ({ ...current, body: event.target.value }))}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm leading-6 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl bg-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-300"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={sending}
            className="rounded-2xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {sending ? "Sending..." : "Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactEditorModal({ contact, saving, onChange, onClose, onSave }) {
  const updateContact = (field, value) => {
    onChange((current) => ({
      ...current,
      [field]: value
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative mx-4 w-full max-w-3xl rounded-[28px] border border-slate-200 bg-white p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
        >
          x
        </button>

        <div>
          <h2 className="text-xl font-bold text-slate-900">{contact?.name ? "Edit Contact" : "Add Contact"}</h2>
          <p className="mt-1 text-sm text-slate-500">
            Capture role, email, WhatsApp, and remarks so the record can be reused everywhere.
          </p>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">Role</label>
            <select
              value={contact.role}
              onChange={(event) => updateContact("role", event.target.value)}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
            >
              {CONTACT_ROLE_OPTIONS.map((roleOption) => (
                <option key={roleOption} value={roleOption}>
                  {roleOption}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">Contact Person Name</label>
            <input
              type="text"
              value={contact.name}
              onChange={(event) => updateContact("name", event.target.value)}
              placeholder="Contact person name"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">Mobile Number</label>
            <input
              type="text"
              value={contact.mobileNumber}
              onChange={(event) => updateContact("mobileNumber", event.target.value)}
              placeholder="Mobile number"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">Email Address</label>
            <input
              type="email"
              value={contact.email}
              onChange={(event) => updateContact("email", event.target.value)}
              placeholder="Email address"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">WhatsApp Number</label>
            <input
              type="text"
              value={contact.whatsappNumber}
              onChange={(event) => updateContact("whatsappNumber", event.target.value)}
              placeholder="WhatsApp number"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">Remarks</label>
            <input
              type="text"
              value={contact.remarks}
              onChange={(event) => updateContact("remarks", event.target.value)}
              placeholder="Optional remarks"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
            />
          </div>
        </div>

        <label className="mt-5 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
          <input
            type="checkbox"
            checked={Boolean(contact.isPrimary)}
            onChange={(event) => updateContact("isPrimary", event.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
          />
          Mark as Primary Contact
        </label>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl bg-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="rounded-2xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {saving ? "Saving..." : "Save Contact"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SupplyField({ label, ...props }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">{label}</label>
      <input
        {...props}
        className="w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-amber-400"
      />
    </div>
  );
}
